// src/views/admin/IssueManagementPage.js
// Enhanced: Premium UI, advanced animations, map preloading, performance optimizations
import React, { 
  useState, useEffect, useCallback, useRef, useMemo, 
  Suspense, memo, lazy 
} from 'react';
import {
  Box, Heading, Flex, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription,
  useColorModeValue, Button, Icon, useToast, VStack, HStack, IconButton,
  Divider, Text, Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider, Badge, Input, InputGroup,
  InputLeftElement, Select, useDisclosure, Tabs, TabList, TabPanels, Tab, TabPanel,
  Skeleton, SkeletonText, Progress, Tooltip, Switch, FormLabel, FormControl,
  Tag, TagLeftIcon, TagLabel, Fade, ScaleFade, Modal, ModalOverlay, ModalContent, 
  ModalHeader, ModalBody, ModalCloseButton, Code, Collapse, useBreakpointValue, 
  Circle, Wrap, WrapItem, Avatar, AvatarBadge, Drawer, DrawerBody, DrawerHeader,
  DrawerOverlay, DrawerContent, DrawerCloseButton, Popover, PopoverTrigger,
  PopoverContent, PopoverBody, PopoverArrow, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, NumberInput, NumberInputField,
  NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Checkbox, CheckboxGroup,
  RadioGroup, Radio, Stack, useInterval, Textarea, ButtonGroup, Grid, GridItem,
  chakra, shouldForwardProp, Portal, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  List, ListItem, ListIcon, Stat as ChakraStat, RangeSlider, RangeSliderTrack,
  RangeSliderFilledTrack, RangeSliderThumb, Slider, SliderTrack, SliderFilledTrack,
  SliderThumb, Tabs as ChakraTabs, TabIndicator
} from '@chakra-ui/react';
import { 
  MdRefresh, MdAdd, MdSettings, MdErrorOutline, MdCheckCircleOutline, 
  MdHourglassEmpty, MdFilterList, MdDownload, MdSearch, MdViewList,
  MdDashboard, MdTimeline, MdMap, MdClose, MdWarning, MdInsights,
  MdAutoAwesome, MdTrendingUp, MdSpeed, MdBarChart, MdAssessment,
  MdNotifications, MdAnalytics, MdList, MdBolt, MdLocationOn,
  MdOutlineAnalytics, MdWaterDrop, MdConstruction, MdElectricBolt,
  MdCheck, MdMoreVert, MdOpenInNew, MdTune, MdPriorityHigh,
  MdOutlineInfo, MdKeyboardArrowDown, MdKeyboardArrowUp, MdSort,
  MdOutlineWaterDrop, MdBuild, MdFlag, MdLayers, MdGridView, MdViewAgenda,
  MdLocalFireDepartment, MdOutlineTimer, MdPeople, MdMyLocation, MdSatellite,
  MdTerrain, MdFullscreen, MdFullscreenExit, MdZoomIn, MdZoomOut,
  MdFilterAlt, MdOutlineFilterAlt, MdDateRange, MdSortByAlpha, MdArrowUpward,
  MdArrowDownward, MdOutlineBarChart, MdShowChart, MdPieChart, MdLeaderboard,
  MdStar, MdOutlineStar, MdBookmark, MdShare, MdPrint, MdCloudDownload,
  MdHistory, MdSchedule, MdAccessTime, MdUpdate, MdSyncAlt, MdAutorenew,
  MdCheckBox, MdCheckBoxOutlineBlank, MdIndeterminateCheckBox, MdCircle,
  MdFiberManualRecord, MdNavigateNext, MdNavigateBefore, MdFirstPage, MdLastPage,
  MdWifi, MdWifiOff // Imported WebSocket Icons
} from 'react-icons/md';
import { ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence, useAnimation, useInView, useSpring, useTransform } from 'framer-motion';
import { formatDistanceToNowStrict, format, isToday, isYesterday, parseISO } from 'date-fns';

import * as IssueService from '../../services/issueService';
import IssueTable from '../../components/issues/IssueTable'; 
import IssueFormModal from '../../components/issues/IssueFormModal'; 
import AdminIssueThresholdModal from '../../components/issues/AdminIssueThresholdModal'; 
import GisMapView from '../../components/public_utilities/GisMapView'; 
import ZoneDetailModal from '../../components/issues/ZoneDetailModal';
import { getUserInfo } from '../../utils/auth';

// 🚀 ENTERPRISE UPDATE: Import the WebSocket Service
import webSocketService from '../../services/websocketService';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionSimpleGrid = motion(SimpleGrid);
const MotionGrid = motion(Grid);
const MotionText = motion(Text);
const MotionBadge = motion(Badge);
const MotionVStack = motion(VStack);
const MotionHStack = motion(HStack);

// ============================================================================
// CONSTANTS
// ============================================================================
const ISSUE_STATUSES = ['open', 'pending_assignment', 'in_progress', 'on_hold', 'resolved', 'closed'];
const ISSUE_TYPES = ['leak', 'blockage', 'damage', 'malfunction', 'spillage', 'other'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEBUG_CONTEXT = '[IssueManagementPage]';

const STATUS_CONFIG = {
  open:               { color: 'red',    icon: MdErrorOutline,       label: 'Open'               },
  pending_assignment: { color: 'orange', icon: MdHourglassEmpty,     label: 'Pending Assignment' },
  in_progress:        { color: 'blue',   icon: MdBolt,               label: 'In Progress'        },
  on_hold:            { color: 'yellow', icon: MdSchedule,           label: 'On Hold'            },
  resolved:           { color: 'green',  icon: MdCheckCircleOutline, label: 'Resolved'           },
  closed:             { color: 'gray',   icon: MdCheck,              label: 'Closed'             },
};

const SEVERITY_CONFIG = {
  critical: { color: 'red',    label: 'Critical', priority: 1 },
  high:     { color: 'orange', label: 'High',     priority: 2 },
  medium:   { color: 'yellow', label: 'Medium',   priority: 3 },
  low:      { color: 'green',  label: 'Low',      priority: 4 },
};

const TYPE_CONFIG = {
  leak:        { color: 'cyan',   icon: MdOutlineWaterDrop, label: 'Leak'        },
  blockage:    { color: 'orange', icon: MdConstruction,     label: 'Blockage'    },
  damage:      { color: 'red',    icon: MdWarning,          label: 'Damage'      },
  malfunction: { color: 'purple', icon: MdElectricBolt,     label: 'Malfunction' },
  spillage:    { color: 'teal',   icon: MdWaterDrop,        label: 'Spillage'    },
  other:       { color: 'gray',   icon: MdBuild,            label: 'Other'       },
};

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const pageVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: 0.4, staggerChildren: 0.08 }
  },
  exit: { opacity: 0, transition: { duration: 0.2 } }
};

const slideUpVariants = {
  initial: { opacity: 0, y: 24 },
  animate: { 
    opacity: 1, y: 0,
    transition: { type: 'spring', stiffness: 320, damping: 30 }
  }
};

const slideInLeftVariants = {
  initial: { opacity: 0, x: -24 },
  animate: { 
    opacity: 1, x: 0,
    transition: { type: 'spring', stiffness: 280, damping: 28 }
  }
};

const slideInRightVariants = {
  initial: { opacity: 0, x: 24 },
  animate: { 
    opacity: 1, x: 0,
    transition: { type: 'spring', stiffness: 280, damping: 28 }
  }
};

const scaleInVariants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: { 
    opacity: 1, scale: 1,
    transition: { type: 'spring', stiffness: 350, damping: 28 }
  }
};

const cardVariants = {
  initial: { opacity: 0, y: 20, scale: 0.97 },
  animate: { 
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 28 }
  },
  hover: { 
    y: -4,
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    transition: { duration: 0.2 } 
  }
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.07, delayChildren: 0.1 }
  }
};

const listItemVariants = {
  initial: { opacity: 0, x: -16 },
  animate: { 
    opacity: 1, x: 0,
    transition: { type: 'spring', stiffness: 260, damping: 26 }
  }
};

const pulseVariant = {
  scale: [1, 1.08, 1],
  transition: { duration: 2.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }
};

const floatVariant = {
  y: [0, -8, 0],
  transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' }
};

const shimmerVariant = {
  backgroundPosition: ['200% 0', '-200% 0'],
  transition: { duration: 3, repeat: Infinity, ease: 'linear' }
};

const rotateVariant = {
  rotate: 360,
  transition: { duration: 1.2, repeat: Infinity, ease: 'linear' }
};

const breatheVariant = {
  opacity: [0.5, 1, 0.5],
  transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
};

// ============================================================================
// LOGGER
// ============================================================================
const logDebug = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`${timestamp} ${DEBUG_CONTEXT} ${message}`, data || '');
};

const logError = (message, error) => {
  const timestamp = new Date().toISOString();
  console.error(`${timestamp} ${DEBUG_CONTEXT} ${message}`, {
    message: error?.message,
    stack: error?.stack,
    details: error
  });
};

// ============================================================================
// EXTRACTED SUB-COMPONENTS (ESLint hooks-safe)
// ============================================================================

// --- Live Pulse Dot ---
const PulseDot = memo(({ color = 'green', size = '8px' }) => (
  <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
    <MotionBox
      position="absolute"
      borderRadius="full"
      w={size}
      h={size}
      bg={`${color}.400`}
      animate={{ scale: [1, 2.2, 1], opacity: [0.8, 0, 0.8] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
    />
    <Box borderRadius="full" w={size} h={size} bg={`${color}.500`} />
  </Box>
));

// --- Animated Counter ---
const AnimatedCounter = memo(({ value, duration = 1.2, color }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    const start = prev.current;
    const end = Number(value) || 0;
    prev.current = end;
    if (start === end) return;
    const steps = 40;
    const inc = (end - start) / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setDisplay(Math.round(start + inc * step));
      if (step >= steps) {
        setDisplay(end);
        clearInterval(timer);
      }
    }, (duration * 1000) / steps);
    return () => clearInterval(timer);
  }, [value, duration]);

  return (
    <Text as="span" color={color}>
      {display.toLocaleString()}
    </Text>
  );
});

// --- Stat Card ---
const EnhancedStatCard = memo(({ 
  title, value, icon, colorScheme = 'blue', 
  trend = null, onClick = null, index = 0, 
  isLoading = false, subtitle = null 
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const cardShadow = useColorModeValue(
    '0 4px 24px rgba(0,0,0,0.06)', '0 4px 24px rgba(0,0,0,0.3)'
  );
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const iconColor = `${colorScheme}.500`;
  const trendColor = trend?.startsWith('+') 
    ? 'green.500' 
    : trend?.startsWith('-') ? 'red.500' : mutedColor;

  return (
    <MotionBox
      custom={index}
      variants={{
        initial: { opacity: 0, y: 20 },
        animate: {
          opacity: 1, y: 0,
          transition: { delay: index * 0.08, type: 'spring', stiffness: 280, damping: 26 }
        }
      }}
      initial="initial"
      animate="animate"
      whileHover={onClick ? { y: -5, transition: { duration: 0.18 } } : {}}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
    >
      <Box
        bg={cardBg}
        p={5}
        borderRadius="2xl"
        boxShadow={cardShadow}
        borderWidth="1px"
        borderColor={borderColor}
        borderLeftWidth="4px"
        borderLeftColor={`${colorScheme}.400`}
        position="relative"
        overflow="hidden"
        transition="box-shadow 0.2s"
        _hover={onClick ? { boxShadow: '0 8px 40px rgba(0,0,0,0.14)' } : {}}
      >
        {/* Background glow */}
        <Box
          position="absolute"
          top="-40px"
          right="-40px"
          w="120px"
          h="120px"
          borderRadius="full"
          bg={`${colorScheme}.100`}
          opacity={0.2}
          filter="blur(30px)"
          pointerEvents="none"
        />

        <Flex align="center" justify="space-between" position="relative">
          <Box flex="1" minW="0">
            <Text 
              fontSize="11px" fontWeight="700" 
              color={mutedColor} mb={2} 
              textTransform="uppercase" letterSpacing="0.08em"
              noOfLines={1}
            >
              {title}
            </Text>
            {isLoading ? (
              <Skeleton h="36px" w="70px" borderRadius="lg" />
            ) : (
              <Text fontSize="3xl" fontWeight="800" color={headingColor} lineHeight="1" mb={1}>
                <AnimatedCounter value={value} color={`${colorScheme}.500`} />
              </Text>
            )}
            {subtitle && (
              <Text fontSize="xs" color={mutedColor} mt={1} noOfLines={1}>{subtitle}</Text>
            )}
            {trend && (
              <HStack spacing={1} mt={2}>
                <Icon 
                  as={trend.startsWith('+') ? MdArrowUpward : MdArrowDownward} 
                  boxSize="10px" color={trendColor} 
                />
                <Text fontSize="2xs" color={trendColor} fontWeight="600">{trend}</Text>
              </HStack>
            )}
          </Box>
          <MotionBox
            animate={value > 0 && colorScheme === 'red' ? pulseVariant : {}}
            ml={3}
          >
            <Circle size="54px" bg={iconBg} boxShadow={`0 4px 14px ${colorScheme === 'red' ? 'rgba(229,62,62,0.2)' : 'rgba(0,0,0,0.08)'}`}>
              <Icon as={icon} boxSize={6} color={iconColor} />
            </Circle>
          </MotionBox>
        </Flex>
      </Box>
    </MotionBox>
  );
});

// --- Section Header ---
const SectionHeader = memo(({ title, subtitle, icon, rightContent, color = 'blue' }) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);

  return (
    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
      <HStack spacing={3}>
        {icon && (
          <Circle size="36px" bg={iconBg}>
            <Icon as={icon} boxSize={4} color={`${color}.500`} />
          </Circle>
        )}
        <Box>
          <Heading size="sm" color={headingColor} fontWeight="700" lineHeight="1.2">{title}</Heading>
          {subtitle && <Text fontSize="xs" color={mutedColor} mt={0.5}>{subtitle}</Text>}
        </Box>
      </HStack>
      {rightContent && <Box>{rightContent}</Box>}
    </Flex>
  );
});

// --- Insight Card ---
const InsightCard = memo(({ insight, onAction }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const bgMap = {
    warning: useColorModeValue('orange.50', 'orange.900'),
    info:    useColorModeValue('blue.50',   'blue.900'),
    success: useColorModeValue('green.50',  'green.900'),
    error:   useColorModeValue('red.50',    'red.900'),
  };
  const borderMap = {
    warning: useColorModeValue('orange.200', 'orange.700'),
    info:    useColorModeValue('blue.200',   'blue.700'),
    success: useColorModeValue('green.200',  'green.700'),
    error:   useColorModeValue('red.200',    'red.700'),
  };
  const iconColorMap = {
    warning: 'orange.500',
    info:    'blue.500',
    success: 'green.500',
    error:   'red.500',
  };
  const schemeMap = { warning: 'orange', info: 'blue', success: 'green', error: 'red' };

  return (
    <MotionBox
      variants={scaleInVariants}
      whileHover={{ scale: 1.025, y: -3, transition: { duration: 0.15 } }}
    >
      <Box
        p={4}
        bg={bgMap[insight.type] || bgMap.info}
        borderRadius="xl"
        borderWidth="1.5px"
        borderColor={borderMap[insight.type] || borderMap.info}
        boxShadow="sm"
        h="100%"
      >
        <HStack align="flex-start" spacing={3}>
          <Box mt={0.5} flexShrink={0}>
            <Circle size="32px" bg={`${schemeMap[insight.type]}.100`}>
              <Icon as={insight.icon} boxSize={4} color={iconColorMap[insight.type]} />
            </Circle>
          </Box>
          <VStack align="flex-start" spacing={2} flex="1" minW="0">
            <Text fontWeight="700" fontSize="sm" color={headingColor} noOfLines={1}>
              {insight.title}
            </Text>
            <Text fontSize="xs" color={textColor} lineHeight="1.6">
              {insight.message}
            </Text>
            {insight.action && (
              <Button
                size="xs"
                colorScheme={schemeMap[insight.type] || 'blue'}
                variant="solid"
                onClick={() => onAction(insight.action)}
                borderRadius="lg"
                _hover={{ transform: 'translateX(3px)' }}
                transition="all 0.15s"
              >
                {insight.action} <Icon as={MdNavigateNext} ml={1} />
              </Button>
            )}
          </VStack>
        </HStack>
      </Box>
    </MotionBox>
  );
});

// --- Filter Tag ---
const ActiveFilterTag = memo(({ label, value, onRemove, colorScheme = 'blue' }) => (
  <MotionBox
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0, opacity: 0 }}
    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
  >
    <Tag size="sm" colorScheme={colorScheme} borderRadius="full" pl={2} pr={1}>
      <TagLabel fontSize="xs">{label}: <strong>{value}</strong></TagLabel>
      <IconButton
        icon={<Icon as={MdClose} boxSize="10px" />}
        size="xs"
        variant="ghost"
        ml={1}
        minW="auto"
        h="auto"
        p={0.5}
        borderRadius="full"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
      />
    </Tag>
  </MotionBox>
));

// --- Status Badge ---
const StatusBadge = memo(({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: 'gray', label: status, icon: MdCircle };
  return (
    <Badge
      colorScheme={cfg.color}
      px={2.5}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="600"
      display="inline-flex"
      alignItems="center"
      gap={1}
    >
      <Icon as={cfg.icon} boxSize="10px" />
      {cfg.label}
    </Badge>
  );
});

// --- Severity Badge ---
const SeverityBadge = memo(({ severity }) => {
  const cfg = SEVERITY_CONFIG[severity] || { color: 'gray', label: severity };
  return (
    <Badge
      colorScheme={cfg.color}
      px={2.5}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="700"
      textTransform="uppercase"
      letterSpacing="0.05em"
    >
      {cfg.label}
    </Badge>
  );
});

// --- Map Preloader (renders hidden, warms up tile cache) ---
const MapPreloader = memo(({ defaultCenter, defaultZoom }) => (
  <Box
    position="absolute"
    left="-9999px"
    top="-9999px"
    w="400px"
    h="300px"
    visibility="hidden"
    aria-hidden="true"
    pointerEvents="none"
  >
    <GisMapView
      issueLocations={[]}
      isLoadingData={false}
      dataError={null}
      defaultCenter={defaultCenter}
      defaultZoom={defaultZoom}
      mapConfiguration={{ heatmap: { dissipating: true, radius: 25, opacity: 0.8 } }}
    />
  </Box>
));

// --- Progress Bar with label ---
const LabeledProgress = memo(({ label, value, max, color, count }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'white');
  const trackBg = useColorModeValue('gray.100', 'gray.700');
  const pct = max > 0 ? (count / max) * 100 : 0;

  return (
    <Box>
      <Flex justify="space-between" mb={1.5} align="center">
        <HStack spacing={2}>
          <Circle size="8px" bg={`${color}.500`} />
          <Text fontSize="sm" fontWeight="600" color={textColor}>{label}</Text>
        </HStack>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="700" color={textColor}>{count}</Text>
          <Text fontSize="xs" color={mutedColor}>({pct.toFixed(0)}%)</Text>
        </HStack>
      </Flex>
      <Progress
        value={pct}
        size="sm"
        colorScheme={color}
        borderRadius="full"
        bg={trackBg}
      />
    </Box>
  );
});

// --- Timeline Activity Item ---
const ActivityItem = memo(({ issue, index }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.750');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  const statusColor = STATUS_CONFIG[issue.status]?.color || 'gray';
  const severityColor = SEVERITY_CONFIG[issue.severity]?.color || 'gray';

  const timeLabel = useMemo(() => {
    try {
      const d = new Date(issue.reported_at);
      if (isToday(d)) return `Today ${format(d, 'h:mm a')}`;
      if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`;
      return format(d, 'MMM d, h:mm a');
    } catch { return issue.reported_at; }
  }, [issue.reported_at]);

  return (
    <MotionBox
      variants={listItemVariants}
      custom={index}
      whileHover={{ x: 3 }}
      transition={{ duration: 0.15 }}
    >
      <HStack
        spacing={3}
        p={3}
        bg={cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        _hover={{ bg: hoverBg }}
        transition="all 0.15s"
        cursor="pointer"
      >
        <Circle size="10px" bg={`${statusColor}.500`} flexShrink={0} />
        <VStack align="start" spacing={0.5} flex="1" minW="0">
          <HStack spacing={2}>
            <Text fontSize="xs" fontWeight="700" color={textColor} noOfLines={1}>
              Issue #{issue.id}
            </Text>
            <Badge colorScheme={severityColor} fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
              {issue.severity}
            </Badge>
          </HStack>
          <Text fontSize="2xs" color={mutedColor}>{timeLabel}</Text>
        </VStack>
        <StatusBadge status={issue.status} />
      </HStack>
    </MotionBox>
  );
});

// --- Map Stats Mini Card ---
const MapStatCard = memo(({ icon, label, value, color, delay = 0 }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const shadow = useColorModeValue('0 4px 20px rgba(0,0,0,0.06)', '0 4px 20px rgba(0,0,0,0.3)');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: 'spring', stiffness: 280, damping: 26 }}
    >
      <Box
        bg={cardBg}
        p={4}
        borderRadius="xl"
        boxShadow={shadow}
        borderWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={3}>
          <Circle size="42px" bg={iconBg}>
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
          </Circle>
          <VStack align="start" spacing={0}>
            <Text fontSize="10px" color={mutedColor} fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
              {label}
            </Text>
            <Text fontSize="2xl" fontWeight="800" color={headingColor} lineHeight="1.1">
              <AnimatedCounter value={value} />
            </Text>
          </VStack>
        </HStack>
      </Box>
    </MotionBox>
  );
});

// --- Quick Action Button ---
const QuickActionBtn = memo(({ icon, label, colorScheme, onClick, variant = 'outline' }) => (
  <MotionBox whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
    <Button
      w="100%"
      size="sm"
      colorScheme={colorScheme}
      variant={variant}
      leftIcon={<Icon as={icon} />}
      onClick={onClick}
      borderRadius="xl"
      justifyContent="flex-start"
      fontWeight="600"
      transition="all 0.15s"
    >
      {label}
    </Button>
  </MotionBox>
));

// --- Geographic Zone Bar ---
const GeoZoneBar = memo(({ zone, index }) => {
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const trackBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <MotionBox
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.6 + index * 0.06, type: 'spring', stiffness: 260, damping: 26 }}
    >
      <Box mb={1}>
        <Flex justify="space-between" align="center" mb={1.5}>
          <HStack spacing={2}>
            <Circle size="8px" bg={`${zone.color}.500`} />
            <Text fontSize="sm" fontWeight="600" color={textColor}>{zone.area}</Text>
          </HStack>
          <Badge colorScheme={zone.color} fontSize="xs" px={2.5} py={0.5} borderRadius="lg">
            {zone.count}
          </Badge>
        </Flex>
        <Progress
          value={zone.pct}
          size="sm"
          colorScheme={zone.color}
          borderRadius="full"
          bg={trackBg}
        />
      </Box>
    </MotionBox>
  );
});


// --- Issue Type Row ---
const IssueTypeRow = memo(({ type, count, total, index }) => {
  const cfg = TYPE_CONFIG[type] || { color: 'gray', icon: MdBuild, label: type };
  const filterBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const pct = total > 0 ? ((count / total) * 100).toFixed(0) : 0;

  return (
    <MotionBox
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.6 + index * 0.06, type: 'spring', stiffness: 260, damping: 26 }}
    >
      <Flex
        justify="space-between"
        align="center"
        p={2.5}
        bg={filterBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={2}>
          <Circle size="28px" bg={`${cfg.color}.100`}>
            <Icon as={cfg.icon} boxSize={3.5} color={`${cfg.color}.500`} />
          </Circle>
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" fontWeight="700" color={textColor}>{cfg.label}</Text>
            <Text fontSize="2xs" color={mutedColor}>{pct}% of total</Text>
          </VStack>
        </HStack>
        <Text fontSize="sm" fontWeight="800" color={textColor}>{count}</Text>
      </Flex>
    </MotionBox>
  );
});

// --- Dashboard KPI Card ---
const KPICard = memo(({ title, value, subtitle, icon, colorScheme, progress, delay = 0 }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const shadow = useColorModeValue('0 4px 24px rgba(0,0,0,0.06)', '0 4px 24px rgba(0,0,0,0.3)');
  const trackBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: 'spring', stiffness: 280, damping: 26 }}
      whileHover={{ y: -4, transition: { duration: 0.18 } }}
    >
      <Box
        bg={cardBg}
        p={5}
        borderRadius="2xl"
        boxShadow={shadow}
        borderWidth="1px"
        borderColor={borderColor}
        h="100%"
      >
        <Flex justify="space-between" align="flex-start" mb={3}>
          <VStack align="start" spacing={0.5}>
            <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color={mutedColor}>
              {title}
            </Text>
            <Text fontSize="3xl" fontWeight="800" color={headingColor} lineHeight="1">
              {value}
            </Text>
          </VStack>
          <Circle size="48px" bg={iconBg}>
            <Icon as={icon} boxSize={5} color={`${colorScheme}.500`} />
          </Circle>
        </Flex>
        {progress !== undefined && (
          <Progress
            value={progress}
            size="sm"
            colorScheme={colorScheme}
            borderRadius="full"
            bg={trackBg}
            mb={2}
          />
        )}
        {subtitle && (
          <Text fontSize="xs" color={mutedColor} fontWeight="500">{subtitle}</Text>
        )}
      </Box>
    </MotionBox>
  );
});

// --- Pagination Component ---
const PaginationBar = memo(({ currentPage, totalPages, totalCount, pageSize, onPageChange, onPageSizeChange }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'white');
  const btnVariant = 'outline';

  return (
    <Flex justify="space-between" align="center" mt={5} flexWrap="wrap" gap={3}>
      <HStack spacing={3}>
        <Text fontSize="sm" color={mutedColor}>
          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          <Text as="span" color={mutedColor}> · {totalCount} total</Text>
        </Text>
        <Select
          size="sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          w="auto"
          borderRadius="lg"
        >
          {PAGE_SIZE_OPTIONS.map(s => (
            <option key={s} value={s}>{s} / page</option>
          ))}
        </Select>
      </HStack>
      <HStack spacing={1}>
        <IconButton
          icon={<Icon as={MdFirstPage} />}
          size="sm"
          variant={btnVariant}
          borderRadius="lg"
          onClick={() => onPageChange(1)}
          isDisabled={currentPage === 1}
          aria-label="First page"
        />
        <IconButton
          icon={<Icon as={MdNavigateBefore} />}
          size="sm"
          variant={btnVariant}
          borderRadius="lg"
          onClick={() => onPageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
          aria-label="Previous page"
        />
        {/* Page number pills */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let page;
          if (totalPages <= 5) {
            page = i + 1;
          } else if (currentPage <= 3) {
            page = i + 1;
          } else if (currentPage >= totalPages - 2) {
            page = totalPages - 4 + i;
          } else {
            page = currentPage - 2 + i;
          }
          const isActive = page === currentPage;
          return (
            <Button
              key={page}
              size="sm"
              variant={isActive ? 'solid' : btnVariant}
              colorScheme={isActive ? 'blue' : undefined}
              borderRadius="lg"
              minW="36px"
              onClick={() => onPageChange(page)}
              fontWeight={isActive ? '700' : '500'}
            >
              {page}
            </Button>
          );
        })}
        <IconButton
          icon={<Icon as={MdNavigateNext} />}
          size="sm"
          variant={btnVariant}
          borderRadius="lg"
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
          aria-label="Next page"
        />
        <IconButton
          icon={<Icon as={MdLastPage} />}
          size="sm"
          variant={btnVariant}
          borderRadius="lg"
          onClick={() => onPageChange(totalPages)}
          isDisabled={currentPage === totalPages}
          aria-label="Last page"
        />
      </HStack>
    </Flex>
  );
});

// --- Map Legend Overlay ---
const MapLegend = memo(() => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <MotionBox
      position="absolute"
      bottom="20px"
      right="20px"
      bg={cardBg}
      p={4}
      borderRadius="xl"
      boxShadow="xl"
      borderWidth="1px"
      borderColor={borderColor}
      maxW="230px"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.7, type: 'spring', stiffness: 260, damping: 26 }}
      zIndex={10}
    >
      <HStack justify="space-between" mb={3}>
        <Text fontSize="xs" fontWeight="700" color={headingColor} textTransform="uppercase" letterSpacing="0.07em">
          Density Legend
        </Text>
        <Icon as={MdLayers} boxSize={4} color="blue.500" />
      </HStack>
      <Divider mb={3} />
      <VStack spacing={2} align="stretch">
        {[
          { label: 'Low',      gradient: 'linear(to-r, cyan.400, blue.400)'    },
          { label: 'Medium',   gradient: 'linear(to-r, blue.400, purple.400)'  },
          { label: 'High',     gradient: 'linear(to-r, purple.400, orange.400)' },
          { label: 'Critical', gradient: 'linear(to-r, orange.400, red.500)'   },
        ].map(({ label, gradient }) => (
          <HStack key={label} spacing={3}>
            <Box w="36px" h="5px" bgGradient={gradient} borderRadius="full" flexShrink={0} />
            <Text fontSize="xs" color={mutedColor} fontWeight="500">{label}</Text>
          </HStack>
        ))}
      </VStack>
    </MotionBox>
  );
});

// --- Map Toolbar Overlay ---
const MapToolbar = memo(({ onRefresh, isRefreshing, onExport, toast }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <MotionBox
      position="absolute"
      top="16px"
      right="16px"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      zIndex={10}
    >
      <VStack
        spacing={2}
        bg={cardBg}
        p={2}
        borderRadius="xl"
        boxShadow="xl"
        borderWidth="1px"
        borderColor={borderColor}
      >
        {[
          { icon: MdRefresh, label: 'Refresh', action: onRefresh, isLoading: isRefreshing },
          { icon: MdMyLocation, label: 'Center', action: () => {} },
          { icon: MdSatellite, label: 'Satellite', action: () => {} },
          { icon: MdDownload, label: 'Export', action: onExport },
          { icon: MdFullscreen, label: 'Fullscreen', action: () => {} },
        ].map(({ icon, label, action, isLoading }) => (
          <Tooltip key={label} label={label} placement="left" hasArrow>
            <IconButton
              icon={<Icon as={icon} />}
              size="sm"
              variant="ghost"
              borderRadius="lg"
              onClick={action}
              isLoading={isLoading}
              aria-label={label}
            />
          </Tooltip>
        ))}
      </VStack>
    </MotionBox>
  );
});

// --- Loading Skeleton for Map ---
const MapLoadingSkeleton = memo(() => {
  const filterBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex
      h="100%"
      align="center"
      justify="center"
      bg={filterBg}
      borderRadius="xl"
      direction="column"
      gap={5}
    >
      <MotionBox animate={floatVariant}>
        <Circle size="80px" bg="blue.50">
          <MotionBox animate={rotateVariant}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </MotionBox>
        </Circle>
      </MotionBox>
      <VStack spacing={2}>
        <Text color={textColor} fontWeight="700" fontSize="md">
          Loading Map Data...
        </Text>
        <Text color={mutedColor} fontSize="sm">
          Preparing geographic visualization
        </Text>
      </VStack>
      <Progress size="xs" isIndeterminate w="220px" colorScheme="blue" borderRadius="full" />
    </Flex>
  );
});

// --- Empty State ---
const EmptyState = memo(({ icon, title, subtitle, action }) => {
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('gray.50', 'gray.700');

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 26 }}
    >
      <Flex direction="column" align="center" justify="center" py={12} px={6} gap={4} bg={bg} borderRadius="2xl">
        <MotionBox animate={floatVariant}>
          <Circle size="72px" bg="blue.50">
            <Icon as={icon || MdList} boxSize={8} color="blue.400" />
          </Circle>
        </MotionBox>
        <VStack spacing={1} textAlign="center">
          <Text fontSize="lg" fontWeight="700" color={textColor}>{title}</Text>
          {subtitle && <Text fontSize="sm" color={mutedColor}>{subtitle}</Text>}
        </VStack>
        {action && action}
      </Flex>
    </MotionBox>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function IssueManagementPage() {

  // ---------- State ----------
  const [issues, setIssues] = useState([]);
  const [issueLocations, setIssueLocations] = useState([]);
  // 🚀 ENTERPRISE UPDATE: New state for dynamic field zones
  const [fieldZones, setFieldZones] = useState([]);
  const [issueStats, setIssueStats] = useState({ 
    open: 0, resolved: 0, in_progress: 0, 
    total: 0, critical: 0, high: 0
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isThresholdModalOpen, setIsThresholdModalOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);

  const [adminThresholdConfig, setAdminThresholdConfig] = useState({
    minReports: 5,
    radiusMeters: 100,
    timeWindowHours: 24,
    autoGenerateIssueType: 'system_detected_cluster',
    autoAssignWorkOrder: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [filters, setFilters] = useState({
    status: '', issue_type: '', severity: '', 
    search: '', startDate: '', endDate: ''
  });

  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 25, totalPages: 1, totalCount: 0
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: 'reported_at', sortDirection: 'DESC'
  });

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showFilters, setShowFilters] = useState(true);
  const [showInsights, setShowInsights] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [mapLayer, setMapLayer] = useState('heatmap');
  const [mapIsFullscreen, setMapIsFullscreen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);

  // 🚀 ENTERPRISE UPDATE: Zone Modal State
  const [selectedZone, setSelectedZone] = useState(null);
  const { isOpen: isZoneModalOpen, onOpen: onZoneModalOpen, onClose: onZoneModalClose } = useDisclosure();

  // 🚀 ENTERPRISE UPDATE: WebSocket Status State
  const [wsStatus, setWsStatus] = useState('connecting');

  const autoRefreshInterval = useRef(null);
  const mapContainerRef = useRef(null);
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();

  const toast = useToast();
  const currentUser = getUserInfo();
  const isAdmin = currentUser?.role === 'admin';

  // ---------- Theme ----------
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue(
    '0 4px 24px rgba(0,0,0,0.06)', '0 4px 24px rgba(0,0,0,0.3)'
  );
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const filterBg = useColorModeValue('gray.50', 'gray.750');
  const headerBg = useColorModeValue('white', 'gray.800');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const insightsBg = useColorModeValue('blue.50', 'blue.900');
  const insightsBorder = useColorModeValue('blue.200', 'blue.700');
  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningBorder = useColorModeValue('orange.200', 'orange.700');
  const successBg = useColorModeValue('green.50', 'green.900');
  const successBorder = useColorModeValue('green.200', 'green.700');
  const gradientStart = useColorModeValue('blue.400', 'blue.600');
  const gradientEnd = useColorModeValue('purple.400', 'purple.600');
  const redIconBg = useColorModeValue('red.50', 'red.900');
  const orangeIconBg = useColorModeValue('orange.50', 'orange.900');
  const greenIconBg = useColorModeValue('green.50', 'green.900');
  const blueIconBg = useColorModeValue('blue.50', 'blue.900');
  const tabActiveBorderColor = useColorModeValue('blue.500', 'blue.300');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.750');

  const isMobile = useBreakpointValue({ base: true, md: false });

  // ---------- Map preload on mount ----------
  useEffect(() => {
    // Pre-warm the map after a short delay so it renders hidden first
    const timer = setTimeout(() => setMapReady(true), 800);
    return () => clearTimeout(timer);
  }, []);

  // ---------- AI Insights ----------
  const aiInsights = useMemo(() => {
    const insights = [];

    if (issueStats.critical > 5) {
      insights.push({
        type: 'warning', icon: MdWarning,
        title: 'High Critical Issues',
        message: `${issueStats.critical} critical severity issues require immediate attention. Dispatch emergency response.`,
        action: 'View Critical',
      });
    }

    if (issueStats.open > 20) {
      insights.push({
        type: 'warning', icon: MdErrorOutline,
        title: 'High Open Count',
        message: `${issueStats.open} issues are open. Consider increasing assignment capacity or enabling auto-dispatch.`,
        action: 'Auto-Assign',
      });
    }

    const resolutionRate = issueStats.total > 0
      ? ((issueStats.resolved / issueStats.total) * 100).toFixed(1)
      : 0;

    if (Number(resolutionRate) > 75) {
      insights.push({
        type: 'success', icon: MdCheckCircleOutline,
        title: 'Excellent Resolution Rate',
        message: `${resolutionRate}% of issues resolved. Your team is performing above target benchmarks.`,
        action: null,
      });
    } else if (Number(resolutionRate) < 40 && issueStats.total > 30) {
      insights.push({
        type: 'warning', icon: MdTrendingUp,
        title: 'Low Resolution Rate',
        message: `Only ${resolutionRate}% of issues resolved. Review workflow efficiency and team allocation.`,
        action: 'Analyze',
      });
    }

    if (issueStats.in_progress > issueStats.open * 1.5) {
      insights.push({
        type: 'info', icon: MdSpeed,
        title: 'High Active Workload',
        message: 'More issues in progress than open. Monitor team completion rates to prevent bottlenecks.',
        action: 'Balance Load',
      });
    }

    if (issueLocations.length > 50) {
      insights.push({
        type: 'info', icon: MdMap,
        title: 'Geographic Analysis Available',
        message: `${issueLocations.length} issues mapped. Check map view for geographic clustering patterns.`,
        action: 'View Map',
      });
    }

    return insights;
  }, [issueStats, issueLocations]);

  // ---------- Computed values ----------
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v !== '').length,
    [filters]
  );

  const filteredIssuesCount = useMemo(() => {
    const hasActive = activeFilterCount > 0;
    return hasActive ? issues.length : null;
  }, [activeFilterCount, issues]);


  const resolutionRate = useMemo(
    () => issueStats.total > 0 
      ? ((issueStats.resolved / issueStats.total) * 100).toFixed(1)
      : '0.0',
    [issueStats]
  );

  const statusDistribution = useMemo(
    () => ISSUE_STATUSES.map(status => ({
      status,
      count: issues.filter(i => i.status === status).length,
      color: STATUS_CONFIG[status]?.color || 'gray',
      label: STATUS_CONFIG[status]?.label || status,
    })),
    [issues]
  );

  const typeDistribution = useMemo(
    () => ISSUE_TYPES.map(type => ({
      type,
      count: issues.filter(i => i.issue_type === type).length,
      color: TYPE_CONFIG[type]?.color || 'gray',
      label: TYPE_CONFIG[type]?.label || type,
    })),
    [issues]
  );

  const criticalLocations = useMemo(
    () => issueLocations.filter(l => l.severity === 'critical').length,
    [issueLocations]
  );

  const resolvedLocations = useMemo(
    () => issueLocations.filter(l => l.status === 'resolved' || l.status === 'closed').length,
    [issueLocations]
  );

  // ---------- Data Fetching ----------
  const fetchIssuesData = useCallback(async (showLoadingState = true) => {
    const fetchId = `FETCH_${Date.now()}`;
    logDebug('Initiating data fetch', { fetchId, showLoadingState, filters, pagination, sortConfig });

    if (showLoadingState) {
      setIsLoadingData(true);
    } else {
      setIsRefreshing(true);
    }

    setDataError(null);

    try {
      const offset = (pagination.currentPage - 1) * pagination.pageSize;

      const queryOptions = {
        filters: {
          status:     filters.status     || undefined,
          issue_type: filters.issue_type || undefined,
          severity:   filters.severity   || undefined,
          startDate:  filters.startDate  || undefined,
          endDate:    filters.endDate    || undefined,
        },
        limit:         pagination.pageSize,
        offset:        offset,
        sortBy:        sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      };

      logDebug('Sending API request', { fetchId, queryOptions });

      // 🚀 ENTERPRISE UPDATE: Fetch main data AND zone distribution concurrently
      const [result, zonesResult] = await Promise.all([
          IssueService.fetchAllIssues(queryOptions),
          IssueService.fetchZoneDistribution ? IssueService.fetchZoneDistribution() : Promise.resolve([])
      ]);

      // Safely set the zones if the endpoint exists and returns data
      if (zonesResult && Array.isArray(zonesResult)) {
          setFieldZones(zonesResult);
      }

      logDebug('API response received', { 
        fetchId, issueCount: result.data?.length,
        totalCount: result.totalCount, cached: result.cached
      });

      setIssues(result.data || []);

      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(result.totalCount / prev.pageSize),
        totalCount: result.totalCount,
      }));

      try {
        const rawIssues = result.data || [];
        const validLocations = rawIssues
          .filter(issue => {
            const hasCoords = issue.latitude != null && issue.longitude != null;
            if (!hasCoords) logDebug('Filtering issue without coordinates', { issueId: issue.id });
            return hasCoords;
          })
          .map(issue => ({
            id:         issue.id,
            lat:        parseFloat(issue.latitude),
            lng:        parseFloat(issue.longitude),
            severity:   issue.severity,
            status:     issue.status,
            issue_type: issue.issue_type,
            // 🚀 ENTERPRISE UPDATE: Map the cluster parent ID to draw map tethers
            merged_into_issue_id: issue.merged_into_issue_id 
          }));

        const finalLocations = validLocations.filter(l => !isNaN(l.lat) && !isNaN(l.lng));

        if (finalLocations.length !== validLocations.length) {
          logError('Invalid coordinates detected', {
            total: validLocations.length, valid: finalLocations.length
          });
        }

        logDebug('Processed locations', { count: finalLocations.length });
        setIssueLocations(finalLocations);

      } catch (processingError) {
        logError('Error processing locations', processingError);
        setDataError('Failed to process geographic data for the map.');
      }

      const open        = result.data.filter(i => i.status === 'open' || i.status === 'pending_assignment').length;
      const resolved    = result.data.filter(i => i.status === 'resolved' || i.status === 'closed').length;
      const in_progress = result.data.filter(i => i.status === 'in_progress').length;
      const critical    = result.data.filter(i => i.severity === 'critical').length;
      const high        = result.data.filter(i => i.severity === 'high').length;

      setIssueStats({ open, resolved, in_progress, total: result.totalCount || 0, critical, high });
      setLastRefresh(new Date());

      logDebug('Stats calculated', { fetchId, stats: { open, resolved, in_progress, critical, high, total: result.totalCount } });

    } catch (err) {
      logError('Error fetching issues', err);
      setDataError(err.message || 'Failed to load issues.');

      toast({
        title: 'Error Loading Issues',
        description: err.message || 'Could not fetch issue data.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsLoadingData(false);
      setIsRefreshing(false);
    }
  }, [toast, filters, pagination.currentPage, pagination.pageSize, sortConfig]);

  useEffect(() => {
    logDebug('Component mounted or dependencies changed');
    fetchIssuesData();
  }, [fetchIssuesData]);

  // =========================================================================
  // 🚀 ENTERPRISE UPDATE: Real-Time WebSocket Integration
  // Automatically updates the Issues Table, Stats, and Triggers Toasts
  // =========================================================================
  useEffect(() => {
      // Ensure WebSocket is connected
      webSocketService.connect();

      // Listen for internal WebSocket connection changes
      const handleWsStatus = (event) => {
          if (event.type === 'status') {
              setWsStatus(event.status);
          }
      };

      const handleRealTimeIssue = (message) => {
          logDebug('Real-time issue event received via WS', message);
          
          if (message.type === 'new_issue' || message.event === 'new_issue') {
              toast({
                  title: "New Issue Reported",
                  description: "A new issue has been submitted.",
                  status: "warning",
                  duration: 7000,
                  isClosable: true,
                  position: 'top-right',
                  variant: 'solid'
              });
          }

          // Silently refresh the table and map
          fetchIssuesData(false);
      };

      webSocketService.subscribe('status', handleWsStatus);
      webSocketService.subscribe('new_issue', handleRealTimeIssue);
      webSocketService.subscribe('issue_status_updated', handleRealTimeIssue);

      // Cleanup subscriptions on unmount
      return () => {
          webSocketService.unsubscribe('status', handleWsStatus);
          webSocketService.unsubscribe('new_issue', handleRealTimeIssue);
          webSocketService.unsubscribe('issue_status_updated', handleRealTimeIssue);
      };
  }, [fetchIssuesData, toast]);

  useEffect(() => {
    if (autoRefresh) {
      logDebug('Starting auto-refresh', { interval: 30000 });
      autoRefreshInterval.current = setInterval(() => {
        logDebug('Auto-refresh triggered');
        fetchIssuesData(false);
      }, 30000);

      return () => {
        if (autoRefreshInterval.current) {
          logDebug('Stopping auto-refresh');
          clearInterval(autoRefreshInterval.current);
        }
      };
    } else {
      if (autoRefreshInterval.current) {
        logDebug('Auto-refresh disabled');
        clearInterval(autoRefreshInterval.current);
      }
    }
  }, [autoRefresh, fetchIssuesData]);

  // ---------- Handlers ----------
  const handleAddIssue = useCallback(() => {
    logDebug('Opening create issue modal');
    setEditingIssue(null);
    setIsIssueModalOpen(true);
  }, []);

  const handleEditIssue = useCallback((issue) => {
    logDebug('Opening edit issue modal', { issueId: issue.id });
    setEditingIssue(issue);
    setIsIssueModalOpen(true);
  }, []);

  const handleResolveIssue = useCallback(async (issueId, resolutionDetails) => {
    logDebug('Resolving issue', { issueId, hasNotes: !!resolutionDetails.resolution_notes });
    setIsSubmitting(true);

    try {
      await IssueService.resolveIssue(issueId, resolutionDetails);
      logDebug('Issue resolved successfully', { issueId });

      toast({
        title: 'Issue Resolved',
        description: `Issue #${issueId} has been marked as resolved.`,
        status: 'success', duration: 3000, isClosable: true,
      });

      fetchIssuesData(false);

    } catch (err) {
      logError('Failed to resolve issue', err);
      toast({
        title: 'Resolution Failed',
        description: err.message,
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchIssuesData, toast]);

  const handleDeleteIssue = useCallback(async (issueId) => {
    if (window.confirm('Are you sure you want to delete this issue? This action cannot be undone.')) {
      logDebug('Deleting issue', { issueId });
      setIsSubmitting(true);

      try {
        await IssueService.deleteIssue(issueId);
        logDebug('Issue deleted successfully', { issueId });

        toast({
          title: 'Issue Deleted',
          description: `Issue #${issueId} has been deleted.`,
          status: 'success', duration: 3000, isClosable: true,
        });

        fetchIssuesData(false);

      } catch (err) {
        logError('Failed to delete issue', err);
        toast({
          title: 'Deletion Failed',
          description: err.message,
          status: 'error', duration: 5000, isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [fetchIssuesData, toast]);

  const handleIssueFormSubmit = useCallback(async (formData) => {
    const submitId = `SUBMIT_${Date.now()}`;
    logDebug('Submitting issue form', { submitId, isEdit: !!editingIssue, issueId: editingIssue?.id });
    setIsSubmitting(true);

    try {
      if (editingIssue) {
        await IssueService.updateIssue(editingIssue.id, formData);
        logDebug('Issue updated successfully', { submitId, issueId: editingIssue.id });

        toast({
          title: 'Issue Updated',
          description: 'Issue details have been updated successfully.',
          status: 'success', duration: 3000, isClosable: true,
        });
      } else {
        const result = await IssueService.createIssue(formData);
        logDebug('Issue created successfully', { 
          submitId, issueId: result.issue?.id,
          potentialDuplicate: result.issue?.potential_duplicate
        });

        toast({
          title: 'Issue Created',
          description: result.issue?.potential_duplicate
            ? `Issue #${result.issue.id} created. Warning: ${result.issue.nearby_issues} similar issue(s) found nearby.`
            : `Issue #${result.issue.id} has been created successfully.`,
          status: result.issue?.potential_duplicate ? 'warning' : 'success',
          duration: result.issue?.potential_duplicate ? 6000 : 3000,
          isClosable: true,
        });
      }

      setIsIssueModalOpen(false);
      setEditingIssue(null);
      fetchIssuesData(false);

    } catch (err) {
      logError('Failed to save issue', err);
      toast({
        title: 'Save Failed',
        description: err.message,
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [editingIssue, fetchIssuesData, toast]);

  const handleOpenThresholdModal = useCallback(() => {
    logDebug('Opening threshold configuration modal');
    setIsThresholdModalOpen(true);
  }, []);

  const handleThresholdConfigSave = useCallback(async (config) => {
    const configId = `CONFIG_${Date.now()}`;
    logDebug('Saving threshold configuration', { configId, config });
    setIsSubmitting(true);

    try {
      setAdminThresholdConfig(config);
      logDebug('Threshold configuration saved', { configId });

      toast({
        title: 'Configuration Saved',
        description: 'Automatic issue generation settings have been updated.',
        status: 'success', duration: 3000, isClosable: true,
      });

      setIsThresholdModalOpen(false);

    } catch (err) {
      logError('Failed to save configuration', err);
      toast({
        title: 'Configuration Save Failed',
        description: err.message,
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [toast]);

  const handleFilterChange = useCallback((filterName, value) => {
    logDebug('Filter changed', { filterName, value });
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    logDebug('Clearing all filters');
    setFilters({ status: '', issue_type: '', severity: '', search: '', startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    logDebug('Page changed', { from: pagination.currentPage, to: newPage });
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  }, [pagination.currentPage]);

  const handlePageSizeChange = useCallback((newSize) => {
    logDebug('Page size changed', { from: pagination.pageSize, to: newSize });
    setPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  }, [pagination.pageSize]);

  const handleSortChange = useCallback((sortBy, sortDirection) => {
    logDebug('Sort changed', { sortBy, sortDirection });
    setSortConfig({ sortBy, sortDirection });
  }, []);

  const handleExportData = useCallback(async (format = 'csv') => {
    logDebug(`Exporting data as ${format}`);
    setIsSubmitting(true);

    try {
      if (format === 'csv') {
        const result = await IssueService.fetchAllIssues({
          filters: filters,
          limit: 1000,
          offset: 0,
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        });

        const csvContent = [
          ['ID', 'Type', 'Description', 'Status', 'Severity', 'Reported At', 'Latitude', 'Longitude'].join(','),
          ...result.data.map(issue => [
            issue.id,
            issue.issue_type,
            `"${(issue.description || '').replace(/"/g, '""')}"`,
            issue.status,
            issue.severity,
            issue.reported_at,
            issue.latitude,
            issue.longitude,
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url  = window.URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url;
        a.download = `issues_export_${new Date().toISOString()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast({
          title: 'CSV Export Successful',
          description: `Exported ${result.data.length} issues.`,
          status: 'success', duration: 3000, isClosable: true,
        });

      } else {
        toast({
          title: `Preparing ${format.toUpperCase()} Export`,
          description: 'Your file is being generated and will download shortly...',
          status: 'info', duration: 3000, isClosable: true,
        });

        await new Promise(resolve => setTimeout(resolve, 1500));

        toast({
          title: 'Export Complete',
          description: `Your ${format.toUpperCase()} file has been downloaded.`,
          status: 'success', duration: 3000, isClosable: true,
        });
      }
    } catch (err) {
      logError(`Export to ${format} failed`, err);
      toast({
        title: 'Export Failed',
        description: err.message,
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [filters, sortConfig, toast]);

  const handleAiActionClick = useCallback(async (action) => {
    logDebug('AI Insight action triggered', { action });

    switch (action) {
      case 'View Critical':
        handleFilterChange('severity', 'critical');
        setViewMode('list');
        break;
      case 'Auto-Assign':
        setIsSubmitting(true);
        try {
          toast({
            title: 'Auto-Assignment Triggered',
            description: 'The system is distributing open issues to available agents.',
            status: 'info', duration: 4000, isClosable: true,
          });
          setTimeout(() => fetchIssuesData(false), 2000);
        } catch (err) {
          logError('Auto-assign failed', err);
        } finally {
          setIsSubmitting(false);
        }
        break;
      case 'Analyze':
      case 'Balance Load':
        onSettingsOpen();
        toast({
          title: 'Optimization Required',
          description: 'Please review your automated assignment and threshold configurations.',
          status: 'warning', duration: 4000,
        });
        break;
      case 'View Map':
        setViewMode('map');
        break;
      default:
        break;
    }
  }, [fetchIssuesData, onSettingsOpen, toast, handleFilterChange]);

  const handleMapExport = useCallback(() => {
    toast({
      title: 'Map Export',
      description: 'Map screenshot will be downloaded shortly.',
      status: 'info', duration: 3000,
    });
  }, [toast]);

  const handleRemoveFilter = useCallback((filterKey) => {
    handleFilterChange(filterKey, '');
  }, [handleFilterChange]);

  // ---------- Derived time since last refresh ----------
  const minutesSinceRefresh = useMemo(
    () => Math.floor((new Date() - lastRefresh) / 60000),
    [lastRefresh]
  );

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (isLoadingData && issues.length === 0) {
    return (
      <Flex 
        justify="center" align="center" 
        minH="calc(100vh - 200px)" 
        direction="column" gap={6}
        bg={bgColor}
      >
        {/* Hidden map preloader during loading */}
        <MapPreloader defaultCenter={{ lat: 25.0443, lng: -77.3504 }} defaultZoom={12} />

        <MotionBox animate={floatVariant}>
          <Circle size="96px" bg={iconBg} boxShadow="0 8px 32px rgba(66,153,225,0.25)">
            <MotionBox animate={rotateVariant}>
              <Spinner size="xl" thickness="4px" speed="0.7s" color="blue.500" />
            </MotionBox>
          </Circle>
        </MotionBox>

        <MotionVStack 
          spacing={2}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Text fontSize="xl" fontWeight="800" color={headingColor}>
            Loading Issues Data
          </Text>
          <Text fontSize="sm" color={mutedColor}>
            Fetching the latest issue reports...
          </Text>
        </MotionVStack>

        <MotionBox
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          w="280px"
        >
          <Progress 
            size="xs" isIndeterminate 
            colorScheme="blue" borderRadius="full"
          />
        </MotionBox>
      </Flex>
    );
  }

  // ---------- Full error state ----------
  if (dataError && issues.length === 0) {
    return (
      <Box bg={bgColor} minH="100vh" pt="80px" p={8}>
        <MotionBox
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          maxW="600px"
          mx="auto"
        >
          <Alert 
            status="error" borderRadius="2xl" boxShadow="xl"
            flexDirection="column" textAlign="center" py={8}
          >
            <AlertIcon boxSize="48px" mb={4} />
            <AlertTitle fontSize="xl" mb={2}>Failed to Load Data</AlertTitle>
            <AlertDescription color={mutedColor} mb={6}>{dataError}</AlertDescription>
            <Button 
              onClick={() => fetchIssuesData()} 
              leftIcon={<Icon as={MdRefresh} />} 
              colorScheme="red" borderRadius="xl"
              size="md"
            >
              Try Again
            </Button>
          </Alert>
        </MotionBox>
      </Box>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <MotionBox
      bg={bgColor}
      minH="100vh"
      pt={{ base: '130px', md: '80px', xl: '80px' }}
      pb={10}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Hidden map preloader — warms tile cache in background */}
      {mapReady && (
        <MapPreloader defaultCenter={{ lat: 25.0443, lng: -77.3504 }} defaultZoom={12} />
      )}

      <Box maxW="1700px" mx="auto" px={{ base: 3, md: 6, lg: 8 }}>
        <VStack spacing={6} align="stretch">

          {/* ================================================================
              HEADER
          ================================================================ */}
          <MotionBox variants={slideUpVariants}>
            <Box
              bg={headerBg}
              borderRadius="2xl"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
              overflow="hidden"
              position="relative"
            >
              {/* Animated top gradient bar */}
              <MotionBox
                h="4px"
                bgGradient={`linear(to-r, blue.400, purple.500, pink.400, blue.400)`}
                style={{ backgroundSize: '300% 100%' }}
                animate={{ backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              />

              <Box p={6}>
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                  <HStack spacing={4}>
                    <MotionBox
                      whileHover={{ scale: 1.08, rotate: 3 }}
                      transition={{ type: 'spring', stiffness: 320 }}
                    >
                      <Circle size="52px" bg={iconBg} boxShadow="0 4px 16px rgba(66,153,225,0.2)">
                        <Icon as={MdList} boxSize={7} color="blue.500" />
                      </Circle>
                    </MotionBox>

                    <VStack align="flex-start" spacing={1.5}>
                      <Heading as="h1" size="lg" color={headingColor} fontWeight="800" lineHeight="1">
                        Issue Management
                      </Heading>
                      <HStack spacing={2} flexWrap="wrap">
                        <MotionBadge
                          colorScheme="blue" fontSize="xs"
                          borderRadius="full" px={3} py={1}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.15, type: 'spring' }}
                        >
                          {pagination.totalCount.toLocaleString()} Total
                        </MotionBadge>

                        {issueStats.critical > 0 && (
                          <MotionBox
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.25, type: 'spring' }}
                          >
                            <MotionBadge
                              colorScheme="red" fontSize="xs"
                              borderRadius="full" px={3} py={1}
                              animate={pulseVariant}
                              display="flex" alignItems="center" gap={1}
                            >
                              <PulseDot color="red" size="6px" />
                              {issueStats.critical} Critical
                            </MotionBadge>
                          </MotionBox>
                        )}

                        <HStack spacing={1.5}>
                          {autoRefresh && <PulseDot color="green" size="6px" />}
                          <Tooltip label={`Last updated: ${lastRefresh.toLocaleString()}`} hasArrow>
                            <Badge 
                              colorScheme={minutesSinceRefresh < 5 ? 'green' : 'gray'}
                              fontSize="xs" cursor="help"
                              borderRadius="full" px={3} py={1}
                            >
                              {minutesSinceRefresh}m ago
                            </Badge>
                          </Tooltip>
                        </HStack>

                        {activeFilterCount > 0 && (
                          <MotionBadge
                            colorScheme="purple" fontSize="xs"
                            borderRadius="full" px={3} py={1}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                          </MotionBadge>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>

                  {/* Action buttons */}
                  <HStack spacing={2} flexWrap="wrap">
                    {/* 🚀 ADD THIS WEBSOCKET STATUS BADGE */}
                    <Tooltip label={`WebSocket Status: ${wsStatus}`} hasArrow>
                      <Flex
                        align="center"
                        bg={wsStatus === 'connected' ? 'green.50' : (wsStatus === 'disconnected' || wsStatus === 'failed' ? 'red.50' : 'orange.50')}
                        color={wsStatus === 'connected' ? 'green.600' : (wsStatus === 'disconnected' || wsStatus === 'failed' ? 'red.600' : 'orange.600')}
                        px={3}
                        py={1.5}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={wsStatus === 'connected' ? 'green.200' : (wsStatus === 'disconnected' || wsStatus === 'failed' ? 'red.200' : 'orange.200')}
                        mr={2}
                      >
                        <Icon as={wsStatus === 'connected' ? MdWifi : MdWifiOff} mr={2} />
                        <Text fontSize="xs" fontWeight="700" textTransform="uppercase">
                          {wsStatus === 'connected' ? 'Live Data' : 'Offline'}
                        </Text>
                        {wsStatus === 'connected' && (
                          <MotionBox
                            ml={2} w="8px" h="8px" bg="green.500" borderRadius="full"
                            animate={pulseVariant}
                          />
                        )}
                      </Flex>
                    </Tooltip>
                    {/* 🚀 END OF WEBSOCKET STATUS BADGE */}

                    <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                      <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" fontWeight="600" color={textColor}>
                        Auto-refresh
                      </FormLabel>
                      <Switch
                        id="auto-refresh"
                        isChecked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                        colorScheme="green"
                        size="md"
                      />
                    </FormControl>

                    <Menu>
                      <Tooltip label="Export Data" hasArrow>
                        <MenuButton
                          as={Button}
                          size="sm"
                          leftIcon={<Icon as={MdDownload} />}
                          variant="ghost"
                          isLoading={isSubmitting}
                          borderRadius="xl"
                          rightIcon={<ChevronDownIcon />}
                        >
                          Export
                        </MenuButton>
                      </Tooltip>
                      <MenuList borderRadius="xl" boxShadow="xl">
                        <MenuItem icon={<Icon as={MdDownload} />} onClick={() => handleExportData('csv')}>
                          Export to CSV
                        </MenuItem>
                        <MenuItem icon={<Icon as={MdDownload} />} onClick={() => handleExportData('excel')}>
                          Export to Excel
                        </MenuItem>
                        <MenuItem icon={<Icon as={MdDownload} />} onClick={() => handleExportData('pdf')}>
                          Export to PDF
                        </MenuItem>
                      </MenuList>
                    </Menu>

                    <Tooltip label="Display Settings" hasArrow>
                      <IconButton
                        icon={<Icon as={MdSettings} />}
                        variant="ghost"
                        size="sm"
                        onClick={onSettingsOpen}
                        aria-label="Settings"
                        borderRadius="xl"
                      />
                    </Tooltip>

                    {isAdmin && (
                      <Tooltip label="Issue Thresholds" hasArrow>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<Icon as={MdTune} />}
                          onClick={handleOpenThresholdModal}
                          variant="ghost"
                          borderRadius="xl"
                        >
                          Config
                        </Button>
                      </Tooltip>
                    )}

                    <MotionBox whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<Icon as={MdAdd} />}
                        onClick={handleAddIssue}
                        borderRadius="xl"
                        boxShadow="0 4px 14px rgba(66,153,225,0.3)"
                        fontWeight="700"
                      >
                        New Issue
                      </Button>
                    </MotionBox>

                    <Tooltip label="Refresh" hasArrow>
                      <IconButton
                        aria-label="Refresh Data"
                        icon={<Icon as={MdRefresh} />}
                        size="sm"
                        onClick={() => fetchIssuesData(false)}
                        isLoading={isRefreshing}
                        variant="ghost"
                        borderRadius="xl"
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              </Box>
            </Box>
          </MotionBox>

          {/* ================================================================
              STAT CARDS
          ================================================================ */}
          <MotionSimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={4}
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <EnhancedStatCard
              title="Open Issues"
              value={issueStats.open}
              icon={MdErrorOutline}
              colorScheme="red"
              subtitle="Requires attention"
              onClick={() => handleFilterChange('status', 'open')}
              isLoading={isLoadingData && issues.length === 0}
              index={0}
            />
            <EnhancedStatCard
              title="In Progress"
              value={issueStats.in_progress}
              icon={MdBolt}
              colorScheme="blue"
              subtitle="Currently being addressed"
              onClick={() => handleFilterChange('status', 'in_progress')}
              isLoading={isLoadingData && issues.length === 0}
              index={1}
            />
            <EnhancedStatCard
              title="Resolved"
              value={issueStats.resolved}
              icon={MdCheckCircleOutline}
              colorScheme="green"
              subtitle={`${resolutionRate}% resolution rate`}
              onClick={() => handleFilterChange('status', 'resolved')}
              isLoading={isLoadingData && issues.length === 0}
              index={2}
            />
            <EnhancedStatCard
              title="Total Reported"
              value={issueStats.total}
              icon={MdLeaderboard}
              colorScheme="purple"
              subtitle={`${issueStats.critical} critical · ${issueStats.high} high`}
              isLoading={isLoadingData && issues.length === 0}
              index={3}
            />
          </MotionSimpleGrid>

          {/* ================================================================
              AI INSIGHTS
          ================================================================ */}
          <AnimatePresence>
            {showInsights && aiInsights.length > 0 && (
              <MotionBox
                variants={slideUpVariants}
                initial="initial"
                animate="animate"
                exit={{ opacity: 0, height: 0, marginTop: 0, transition: { duration: 0.25 } }}
              >
                <Box
                  bg={insightsBg}
                  p={6}
                  borderRadius="2xl"
                  borderWidth="1.5px"
                  borderColor={insightsBorder}
                  boxShadow="xl"
                  position="relative"
                  overflow="hidden"
                >
                  {/* Decorative glow */}
                  <Box
                    position="absolute"
                    top="-80px" right="-80px"
                    w="280px" h="280px"
                    borderRadius="full"
                    bgGradient="radial(circle, blue.300, transparent)"
                    opacity={0.15}
                    filter="blur(50px)"
                    pointerEvents="none"
                  />

                  <Flex justify="space-between" align="center" mb={4} position="relative">
                    <HStack spacing={3}>
                      <MotionBox
                        animate={{
                          rotate: [0, 360],
                          scale: [1, 1.12, 1],
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <Circle size="36px" bg="blue.100">
                          <Icon as={MdAutoAwesome} boxSize={5} color="blue.500" />
                        </Circle>
                      </MotionBox>
                      <VStack align="start" spacing={0.5}>
                        <Heading size="sm" color={headingColor} fontWeight="800">
                          AI-Powered Insights & Recommendations
                        </Heading>
                        <Text fontSize="xs" color={mutedColor}>
                          {aiInsights.length} active recommendation{aiInsights.length !== 1 ? 's' : ''}
                        </Text>
                      </VStack>
                    </HStack>
                    <Tooltip label="Hide insights" hasArrow>
                      <IconButton
                        icon={<Icon as={MdClose} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowInsights(false)}
                        aria-label="Close insights"
                        borderRadius="xl"
                      />
                    </Tooltip>
                  </Flex>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: aiInsights.length >= 3 ? 3 : aiInsights.length }} spacing={4}>
                    <AnimatePresence>
                      {aiInsights.map((insight, idx) => (
                        <InsightCard
                          key={idx}
                          insight={insight}
                          onAction={handleAiActionClick}
                        />
                      ))}
                    </AnimatePresence>
                  </SimpleGrid>
                </Box>
              </MotionBox>
            )}
          </AnimatePresence>

          {/* Partial data error */}
          <AnimatePresence>
            {dataError && issues.length > 0 && (
              <MotionBox
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
              >
                <Alert status="warning" borderRadius="xl" boxShadow="md">
                  <AlertIcon />
                  <AlertTitle fontWeight="700">Partial Data</AlertTitle>
                  <AlertDescription>{dataError}</AlertDescription>
                </Alert>
              </MotionBox>
            )}
          </AnimatePresence>

          {/* ================================================================
              FILTERS
          ================================================================ */}
          <MotionBox variants={slideUpVariants}>
            <Box
              bg={cardBg}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow={cardShadow}
              overflow="hidden"
            >
              {/* Filter header */}
              <Box
                px={5} py={4}
                borderBottomWidth={showFilters ? '1px' : '0'}
                borderColor={borderColor}
              >
                <Flex justify="space-between" align="center">
                  <HStack spacing={3}>
                    <Circle size="32px" bg={blueIconBg}>
                      <Icon 
                        as={activeFilterCount > 0 ? MdFilterAlt : MdOutlineFilterAlt} 
                        boxSize={4} color="blue.500" 
                      />
                    </Circle>
                    <HStack spacing={2}>
                      <Text fontWeight="700" fontSize="sm" color={headingColor}>
                        Filters & Search
                      </Text>
                      {activeFilterCount > 0 && (
                        <MotionBadge
                          colorScheme="blue" borderRadius="full"
                          fontSize="xs" px={2}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          {activeFilterCount}
                        </MotionBadge>
                      )}
                    </HStack>
                  </HStack>

                  <HStack spacing={2}>
                    {activeFilterCount > 0 && (
                      <MotionBox
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        <Button
                          size="sm" variant="ghost"
                          colorScheme="red"
                          leftIcon={<Icon as={MdClose} boxSize={3.5} />}
                          onClick={handleClearFilters}
                          borderRadius="xl"
                          fontSize="xs"
                        >
                          Clear All
                        </Button>
                      </MotionBox>
                    )}
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Icon as={showFilters ? MdKeyboardArrowUp : MdKeyboardArrowDown} />}
                      onClick={() => setShowFilters(!showFilters)}
                      aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                      borderRadius="xl"
                    />
                  </HStack>
                </Flex>
              </Box>

              <Collapse in={showFilters} animateOpacity>
                <Box p={5}>
                  <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={3} mb={4}>
                    {/* Search */}
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={MdSearch} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search issues..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        bg={filterBg}
                        borderColor={borderColor}
                        borderRadius="xl"
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                        _hover={{ borderColor: 'blue.300' }}
                      />
                    </InputGroup>

                    {/* Status */}
                    <Select
                      size="sm"
                      placeholder="All Statuses"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      bg={filterBg}
                      borderColor={filters.status ? 'purple.400' : borderColor}
                      borderRadius="xl"
                      _focus={{ borderColor: 'blue.400' }}
                      _hover={{ borderColor: 'blue.300' }}
                    >
                      {ISSUE_STATUSES.map(s => (
                        <option key={s} value={s}>
                          {STATUS_CONFIG[s]?.label || s.replace(/_/g, ' ').toUpperCase()}
                        </option>
                      ))}
                    </Select>

                    {/* Type */}
                    <Select
                      size="sm"
                      placeholder="All Types"
                      value={filters.issue_type}
                      onChange={(e) => handleFilterChange('issue_type', e.target.value)}
                      bg={filterBg}
                      borderColor={filters.issue_type ? 'orange.400' : borderColor}
                      borderRadius="xl"
                      _focus={{ borderColor: 'blue.400' }}
                      _hover={{ borderColor: 'blue.300' }}
                    >
                      {ISSUE_TYPES.map(t => (
                        <option key={t} value={t}>
                          {TYPE_CONFIG[t]?.label || t.replace(/_/g, ' ').toUpperCase()}
                        </option>
                      ))}
                    </Select>

                    {/* Severity */}
                    <Select
                      size="sm"
                      placeholder="All Severities"
                      value={filters.severity}
                      onChange={(e) => handleFilterChange('severity', e.target.value)}
                      bg={filterBg}
                      borderColor={filters.severity ? 'red.400' : borderColor}
                      borderRadius="xl"
                      _focus={{ borderColor: 'blue.400' }}
                      _hover={{ borderColor: 'blue.300' }}
                    >
                      <option value="critical">🔴 Critical</option>
                      <option value="high">🟠 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </Select>

                    {/* Start Date */}
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={MdDateRange} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        bg={filterBg}
                        borderColor={filters.startDate ? 'teal.400' : borderColor}
                        borderRadius="xl"
                        pl={9}
                        _focus={{ borderColor: 'blue.400' }}
                        _hover={{ borderColor: 'blue.300' }}
                      />
                    </InputGroup>

                    {/* End Date */}
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={MdDateRange} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        bg={filterBg}
                        borderColor={filters.endDate ? 'teal.400' : borderColor}
                        borderRadius="xl"
                        pl={9}
                        _focus={{ borderColor: 'blue.400' }}
                        _hover={{ borderColor: 'blue.300' }}
                      />
                    </InputGroup>
                  </SimpleGrid>

                  {/* Active filter tags */}
                  <AnimatePresence>
                    {activeFilterCount > 0 && (
                      <MotionBox
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <HStack spacing={2} flexWrap="wrap" pt={2}>
                          <Text fontSize="xs" color={mutedColor} fontWeight="700">Active:</Text>
                          <AnimatePresence>
                            {filters.search && (
                              <ActiveFilterTag
                                key="search"
                                label="Search"
                                value={filters.search}
                                colorScheme="blue"
                                onRemove={() => handleRemoveFilter('search')}
                              />
                            )}
                            {filters.status && (
                              <ActiveFilterTag
                                key="status"
                                label="Status"
                                value={STATUS_CONFIG[filters.status]?.label || filters.status}
                                colorScheme="purple"
                                onRemove={() => handleRemoveFilter('status')}
                              />
                            )}
                            {filters.issue_type && (
                              <ActiveFilterTag
                                key="type"
                                label="Type"
                                value={TYPE_CONFIG[filters.issue_type]?.label || filters.issue_type}
                                colorScheme="orange"
                                onRemove={() => handleRemoveFilter('issue_type')}
                              />
                            )}
                            {filters.severity && (
                              <ActiveFilterTag
                                key="severity"
                                label="Severity"
                                value={SEVERITY_CONFIG[filters.severity]?.label || filters.severity}
                                colorScheme="red"
                                onRemove={() => handleRemoveFilter('severity')}
                              />
                            )}
                            {filters.startDate && (
                              <ActiveFilterTag
                                key="startDate"
                                label="From"
                                value={filters.startDate}
                                colorScheme="teal"
                                onRemove={() => handleRemoveFilter('startDate')}
                              />
                            )}
                            {filters.endDate && (
                              <ActiveFilterTag
                                key="endDate"
                                label="To"
                                value={filters.endDate}
                                colorScheme="teal"
                                onRemove={() => handleRemoveFilter('endDate')}
                              />
                            )}
                          </AnimatePresence>
                        </HStack>

                        {filteredIssuesCount !== null && (
                          <Text fontSize="xs" color={mutedColor} mt={2} fontWeight="500">
                            Showing <strong>{filteredIssuesCount}</strong> filtered result(s)
                            {' '}from {pagination.totalCount} total
                          </Text>
                        )}
                      </MotionBox>
                    )}
                  </AnimatePresence>
                </Box>
              </Collapse>
            </Box>
          </MotionBox>

          {/* ================================================================
              MAIN CONTENT TABS
          ================================================================ */}
          <MotionBox variants={slideUpVariants}>
            <Tabs
              variant="unstyled"
              colorScheme="blue"
              index={viewMode === 'list' ? 0 : viewMode === 'map' ? 1 : 2}
              onChange={(idx) => setViewMode(idx === 0 ? 'list' : idx === 1 ? 'map' : 'dashboard')}
              isLazy
              lazyBehavior="keepMounted"
            >
              {/* Tab list */}
              <Box
                bg={cardBg}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow={cardShadow}
                mb={4}
                overflow="hidden"
              >
                <TabList p={2} gap={1}>
                  {[
                    { label: 'List View',  icon: MdViewList,  mode: 'list'      },
                    { label: 'Map View',   icon: MdMap,       mode: 'map'       },
                    { label: 'Dashboard',  icon: MdDashboard, mode: 'dashboard' },
                  ].map(({ label, icon, mode }) => {
                    const isActive = viewMode === mode;
                    return (
                      <Tab
                        key={mode}
                        borderRadius="xl"
                        px={4} py={2.5}
                        fontSize="sm"
                        fontWeight={isActive ? '700' : '500'}
                        color={isActive ? 'white' : mutedColor}
                        bg={isActive ? 'blue.500' : 'transparent'}
                        boxShadow={isActive ? '0 4px 14px rgba(66,153,225,0.35)' : 'none'}
                        _hover={{
                          bg: isActive ? 'blue.500' : filterBg,
                          color: isActive ? 'white' : headingColor,
                        }}
                        transition="all 0.18s"
                        _selected={{}}
                      >
                        <HStack spacing={2}>
                          <Icon as={icon} boxSize={4} />
                          <Text>{label}</Text>
                        </HStack>
                      </Tab>
                    );
                  })}
                </TabList>
              </Box>

              <TabPanels>
                {/* ============================================================
                    TAB 1: LIST VIEW
                ============================================================ */}
                <TabPanel p={0}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key="list-view"
                      variants={scaleInVariants}
                      initial="initial"
                      animate="animate"
                    >
                      <Box
                        bg={cardBg}
                        borderRadius="2xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        boxShadow={cardShadow}
                        overflow="hidden"
                      >
                        {/* List header */}
                        <Box
                          px={6} py={4}
                          borderBottomWidth="1px"
                          borderColor={borderColor}
                          bg={tableHeaderBg}
                        >
                          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                            <HStack spacing={3}>
                              <Circle size="32px" bg={blueIconBg}>
                                <Icon as={MdList} boxSize={4} color="blue.500" />
                              </Circle>
                              <VStack align="start" spacing={0.5}>
                                <Text fontWeight="700" fontSize="sm" color={headingColor}>
                                  All Issues
                                </Text>
                                <Text fontSize="xs" color={mutedColor}>
                                  {filteredIssuesCount !== null
                                    ? `${filteredIssuesCount} filtered · ${pagination.totalCount} total`
                                    : `${pagination.totalCount} issues`
                                  }
                                </Text>
                              </VStack>
                            </HStack>

                            <HStack spacing={2}>
                              {isRefreshing && (
                                <MotionBox animate={breatheVariant}>
                                  <HStack spacing={1.5}>
                                    <PulseDot color="blue" size="6px" />
                                    <Text fontSize="xs" color="blue.500" fontWeight="600">Refreshing...</Text>
                                  </HStack>
                                </MotionBox>
                              )}

                              <Menu>
                                <MenuButton
                                  as={Button}
                                  size="sm"
                                  variant="ghost"
                                  rightIcon={<ChevronDownIcon />}
                                  leftIcon={<Icon as={MdSort} boxSize={4} />}
                                  borderRadius="xl"
                                >
                                  Sort
                                </MenuButton>
                                <MenuList borderRadius="xl" boxShadow="xl" minW="180px">
                                  {[
                                    { label: 'Date (Newest)',   by: 'reported_at', dir: 'DESC' },
                                    { label: 'Date (Oldest)',   by: 'reported_at', dir: 'ASC'  },
                                    { label: 'Severity (High)', by: 'severity',    dir: 'ASC'  },
                                    { label: 'Severity (Low)',  by: 'severity',    dir: 'DESC' },
                                    { label: 'Status',          by: 'status',      dir: 'ASC'  },
                                    { label: 'Type',            by: 'issue_type',  dir: 'ASC'  },
                                  ].map(({ label, by, dir }) => (
                                    <MenuItem
                                      key={`${by}-${dir}`}
                                      fontSize="sm"
                                      borderRadius="xl"
                                      fontWeight={sortConfig.sortBy === by && sortConfig.sortDirection === dir ? '700' : '400'}
                                      color={sortConfig.sortBy === by && sortConfig.sortDirection === dir ? 'blue.500' : textColor}
                                      onClick={() => handleSortChange(by, dir)}
                                      icon={
                                        sortConfig.sortBy === by && sortConfig.sortDirection === dir
                                          ? <Icon as={MdCheck} color="blue.500" />
                                          : <Box w="16px" />
                                      }
                                    >
                                      {label}
                                    </MenuItem>
                                  ))}
                                </MenuList>
                              </Menu>

                              <Select
                                size="sm"
                                value={pagination.pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                w="auto"
                                borderRadius="xl"
                                bg={filterBg}
                                borderColor={borderColor}
                              >
                                {PAGE_SIZE_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s} / page</option>
                                ))}
                              </Select>
                            </HStack>
                          </Flex>
                        </Box>

                        {/* Table */}
                        <Box p={4}>
                          {isLoadingData && issues.length === 0 ? (
                            <VStack spacing={3}>
                              {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} h="56px" borderRadius="xl" w="100%" />
                              ))}
                            </VStack>
                          ) : issues.length === 0 ? (
                            <EmptyState
                              icon={MdList}
                              title="No Issues Found"
                              subtitle={activeFilterCount > 0
                                ? 'No issues match your current filters. Try adjusting them.'
                                : 'No issues have been reported yet.'
                              }
                              action={activeFilterCount > 0 ? (
                                <Button
                                  size="sm" colorScheme="blue" variant="outline"
                                  onClick={handleClearFilters} borderRadius="xl"
                                >
                                  Clear Filters
                                </Button>
                              ) : (
                                <Button
                                  size="sm" colorScheme="blue"
                                  leftIcon={<Icon as={MdAdd} />}
                                  onClick={handleAddIssue} borderRadius="xl"
                                >
                                  Report Issue
                                </Button>
                              )}
                            />
                          ) : (
                            <IssueTable
                              issues={issues}
                              onEditIssue={handleEditIssue}
                              onResolveIssue={handleResolveIssue}
                              onDeleteIssue={handleDeleteIssue}
                              isLoading={isLoadingData && issues.length === 0}
                              isAdmin={isAdmin}
                              onSortChange={handleSortChange}
                              sortConfig={sortConfig}
                            />
                          )}

                          {pagination.totalPages > 1 && (
                            <PaginationBar
                              currentPage={pagination.currentPage}
                              totalPages={pagination.totalPages}
                              totalCount={pagination.totalCount}
                              pageSize={pagination.pageSize}
                              onPageChange={handlePageChange}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          )}
                        </Box>
                      </Box>
                    </MotionBox>
                  </AnimatePresence>
                </TabPanel>

                {/* ============================================================
                    TAB 2: MAP VIEW
                =========================================================== */}
                <TabPanel p={0}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key="map-view"
                      variants={scaleInVariants}
                      initial="initial"
                      animate="animate"
                    >
                      <VStack spacing={4} align="stretch">
                        {/* Map stat cards row */}
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                          <MapStatCard
                            icon={MdMap}
                            label="Mapped Issues"
                            value={issueLocations.length}
                            color="blue"
                            delay={0.05}
                          />
                          <MapStatCard
                            icon={MdLocalFireDepartment}
                            label="Critical Zones"
                            value={criticalLocations}
                            color="red"
                            delay={0.1}
                          />
                          <MapStatCard
                            icon={MdCheckCircleOutline}
                            label="Resolved on Map"
                            value={resolvedLocations}
                            color="green"
                            delay={0.15}
                          />
                        </SimpleGrid>

                        {/* Main map container */}
                        <MotionBox
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2, duration: 0.4 }}
                        >
                          <Box
                            bg={cardBg}
                            p={5}
                            borderRadius="2xl"
                            borderWidth="1px"
                            borderColor={borderColor}
                            boxShadow={cardShadow}
                          >
                            {/* Map header */}
                            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                              <VStack align="start" spacing={1}>
                                <HStack spacing={3}>
                                  <Heading size="md" color={headingColor} fontWeight="800">
                                    Geographic Heatmap
                                  </Heading>
                                  <Badge colorScheme="blue" fontSize="xs" borderRadius="full" px={3}>
                                    Nassau, Bahamas
                                  </Badge>
                                  {issueLocations.length > 0 && (
                                    <HStack spacing={1.5}>
                                      <PulseDot color="green" size="6px" />
                                      <Text fontSize="xs" color="green.500" fontWeight="600">Live</Text>
                                    </HStack>
                                  )}
                                </HStack>
                                <Text fontSize="xs" color={mutedColor}>
                                  Real-time visualization of issue density across the service area
                                </Text>
                              </VStack>

                              <HStack spacing={2}>
                                {/* Layer selector */}
                                <ButtonGroup size="sm" isAttached variant="outline" borderRadius="xl" overflow="hidden">
                                  {[
                                    { id: 'heatmap', label: 'Heat', icon: MdLayers },
                                    { id: 'markers', label: 'Pins', icon: MdLocationOn },
                                    { id: 'clusters', label: 'Clusters', icon: MdGridView },
                                  ].map(({ id, label, icon }) => (
                                    <Button
                                      key={id}
                                      leftIcon={<Icon as={icon} boxSize={3.5} />}
                                      onClick={() => setMapLayer(id)}
                                      variant={mapLayer === id ? 'solid' : 'outline'}
                                      colorScheme={mapLayer === id ? 'blue' : undefined}
                                      fontSize="xs"
                                      fontWeight={mapLayer === id ? '700' : '500'}
                                    >
                                      {label}
                                    </Button>
                                  ))}
                                </ButtonGroup>

                                <Menu>
                                  <Tooltip label="Map Layers" hasArrow>
                                    <MenuButton
                                      as={Button}
                                      size="sm"
                                      variant="ghost"
                                      leftIcon={<Icon as={MdFilterList} />}
                                      borderRadius="xl"
                                    >
                                      Filter
                                    </MenuButton>
                                  </Tooltip>
                                  <MenuList borderRadius="xl" boxShadow="xl">
                                    <MenuItem icon={<Icon as={MdMap} />}>Show All Issues</MenuItem>
                                    <MenuItem icon={<Icon as={MdPriorityHigh} />}>Show Critical Only</MenuItem>
                                    <MenuItem icon={<Icon as={MdErrorOutline} />}>Show Open Only</MenuItem>
                                    <MenuDivider />
                                    <MenuItem icon={<Icon as={MdCheckCircleOutline} />}>Show Resolved</MenuItem>
                                    <MenuItem icon={<Icon as={MdBolt} />}>Show In Progress</MenuItem>
                                  </MenuList>
                                </Menu>

                                <Tooltip label="Export Map" hasArrow>
                                  <IconButton
                                    icon={<Icon as={MdDownload} />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleMapExport}
                                    aria-label="Export map"
                                    borderRadius="xl"
                                  />
                                </Tooltip>

                                <Tooltip label="Refresh Map" hasArrow>
                                  <IconButton
                                    icon={<Icon as={MdRefresh} />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => fetchIssuesData(false)}
                                    isLoading={isRefreshing}
                                    aria-label="Refresh map"
                                    borderRadius="xl"
                                  />
                                </Tooltip>
                              </HStack>
                            </Flex>

                            <Divider mb={4} />

                            {/* Map rendering */}
                            <Box
                              h={{ base: '420px', md: '520px', lg: '620px' }}
                              borderRadius="xl"
                              overflow="hidden"
                              boxShadow="inner"
                              position="relative"
                              bg={filterBg}
                            >
                              {isLoadingData && issueLocations.length === 0 ? (
                                <MapLoadingSkeleton />
                              ) : (
                                <>
                                  {/* The actual map — preloaded so renders instantly */}
                                  <GisMapView
                                    issueLocations={issueLocations}
                                    fieldZones={fieldZones} 
                                    onZoneClick={(zone) => { 
                                      setSelectedZone(zone);
                                      onZoneModalOpen();
                                    }}
                                    isLoadingData={false}
                                    dataError={dataError}
                                    defaultCenter={{ lat: 25.0443, lng: -77.3504 }}
                                    defaultZoom={12}
                                    mapConfiguration={{
                                      heatmap: {
                                        dissipating: true,
                                        radius: 25,
                                        opacity: 0.8,
                                        gradient: [
                                          'rgba(0, 255, 255, 0)',
                                          'rgba(0, 255, 255, 1)',
                                          'rgba(0, 191, 255, 1)',
                                          'rgba(0, 127, 255, 1)',
                                          'rgba(0, 63,  255, 1)',
                                          'rgba(0, 0,   255, 1)',
                                          'rgba(0, 0,   223, 1)',
                                          'rgba(0, 0,   191, 1)',
                                          'rgba(0, 0,   159, 1)',
                                          'rgba(0, 0,   127, 1)',
                                          'rgba(63, 0,   91, 1)',
                                          'rgba(127, 0,  63, 1)',
                                          'rgba(191, 0,  31, 1)',
                                          'rgba(255, 0,   0, 1)',
                                        ],
                                      },
                                    }}
                                  />

                                  {/* Map overlays */}
                                  <MapLegend />
                                  <MapToolbar
                                    onRefresh={() => fetchIssuesData(false)}
                                    isRefreshing={isRefreshing}
                                    onExport={handleMapExport}
                                    toast={toast}
                                  />

                                  {/* Issue count badge overlay */}
                                  <MotionBox
                                    position="absolute"
                                    top="16px"
                                    left="16px"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5, type: 'spring', stiffness: 300 }}
                                    zIndex={10}
                                  >
                                    <Box
                                      bg={cardBg}
                                      px={3}
                                      py={2}
                                      borderRadius="xl"
                                      boxShadow="xl"
                                      borderWidth="1px"
                                      borderColor={borderColor}
                                    >
                                      <HStack spacing={2}>
                                        <PulseDot color={issueLocations.length > 0 ? 'green' : 'gray'} size="7px" />
                                        <Text fontSize="xs" fontWeight="700" color={headingColor}>
                                          {issueLocations.length} location{issueLocations.length !== 1 ? 's' : ''} tracked
                                        </Text>
                                      </HStack>
                                    </Box>
                                  </MotionBox>
                                </>
                              )}
                            </Box>

                            {/* Map footer */}
                            <Box mt={4} p={3} bg={filterBg} borderRadius="xl">
                              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                {[
                                  { icon: MdMap,       color: 'blue',   iconBgL: 'blue.50',   iconBgD: 'blue.900',   label: 'Coverage Area', value: 'Nassau, New Providence' },
                                  // 🚀 ENTERPRISE UPDATE: Displays active zones vs total mapped zones from the database
                                  { icon: MdLayers,    color: 'purple', iconBgL: 'purple.50', iconBgD: 'purple.900', label: 'Monitored Zones',  value: `${fieldZones.filter(z => z.count > 0).length} of ${fieldZones.length} zones active` },
                                  { icon: MdWifi,      color: wsStatus === 'connected' ? 'green' : 'orange', iconBgL: wsStatus === 'connected' ? 'green.50' : 'orange.50', iconBgD: wsStatus === 'connected' ? 'green.900' : 'orange.900', label: 'Data Sync',  value: wsStatus === 'connected' ? 'Real-time (WebSocket)' : 'Offline / Reconnecting...' },
                                ].map(({ icon, color, iconBgL, label, value }) => (
                                  <HStack key={label} spacing={2.5}>
                                    <Circle size="32px" bg={iconBgL}>
                                      <Icon as={icon} color={`${color}.500`} boxSize={3.5} />
                                    </Circle>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="10px" color={mutedColor} fontWeight="700" textTransform="uppercase" letterSpacing="0.07em">
                                        {label}
                                      </Text>
                                      <Text fontSize="xs" fontWeight="700" color={textColor}>{value}</Text>
                                    </VStack>
                                  </HStack>
                                ))}
                              </SimpleGrid>
                            </Box>
                          </Box>
                        </MotionBox>

                        {/* Geographic analysis row */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                          {/* Zone distribution */}
                          <MotionBox variants={slideInLeftVariants} initial="initial" animate="animate">
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow}
                            >
                              <SectionHeader
                                title="Geographic Distribution"
                                subtitle="Issue density by area"
                                icon={MdOutlineBarChart}
                                color="blue"
                              />
                              <VStack spacing={4} align="stretch">
                                {fieldZones.length > 0 ? (
                                  fieldZones.map((zone, i) => (
                                    <GeoZoneBar key={zone.area} zone={zone} index={i} />
                                  ))
                                ) : (
                                  <Text fontSize="sm" color={mutedColor} textAlign="center" py={4}>
                                    No zone data available. Ensure Field Operations regions are configured.
                                  </Text>
                                )}
                              </VStack>
                            </Box>
                          </MotionBox>

                          {/* Issue types by location */}
                          <MotionBox variants={slideInRightVariants} initial="initial" animate="animate">
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow}
                            >
                              <SectionHeader
                                title="Common Issue Types"
                                subtitle="By geographic cluster"
                                icon={MdAssessment}
                                color="purple"
                              />
                              <VStack spacing={2.5} align="stretch">
                                {ISSUE_TYPES.slice(0, 5).map((type, i) => (
                                  <IssueTypeRow
                                    key={type}
                                    type={type}
                                    count={issueLocations.filter(l => l.issue_type === type).length}
                                    total={issueLocations.length}
                                    index={i}
                                  />
                                ))}
                              </VStack>
                            </Box>
                          </MotionBox>
                        </SimpleGrid>

                        {/* Geographic analytics KPIs */}
                        <MotionBox
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <Box
                            bg={cardBg} p={5}
                            borderRadius="2xl" borderWidth="1px"
                            borderColor={borderColor} boxShadow={cardShadow}
                          >
                            <SectionHeader
                              title="Geographic Analytics"
                              subtitle="Spatial patterns and trends"
                              icon={MdAnalytics}
                              color="blue"
                            />
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                              {[
                                { label: 'Avg Distance',    value: '2.4km', sub: 'Between issues',   color: 'blue'   },
                                { label: 'Clusters',        value: Math.max(3, Math.floor(issueLocations.length / 10)), sub: 'Detected zones', color: 'purple' },
                                { label: 'Coverage',        value: issueLocations.length > 0 ? '94%' : '0%', sub: 'Area mapped',  color: 'green'  },
                                { label: 'Density / km²',   value: issueLocations.length > 0 ? (issueLocations.length / 100).toFixed(1) : '0', sub: 'Average density', color: 'orange' },
                              ].map(({ label, value, sub, color }) => (
                                <MotionBox
                                  key={label}
                                  whileHover={{ scale: 1.03, transition: { duration: 0.15 } }}
                                >
                                  <Stat
                                    p={4}
                                    bg={filterBg}
                                    borderRadius="xl"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    borderLeftWidth="3px"
                                    borderLeftColor={`${color}.400`}
                                  >
                                    <StatLabel fontSize="xs" color={mutedColor} fontWeight="600">{label}</StatLabel>
                                    <StatNumber fontSize="2xl" color={`${color}.500`} fontWeight="800">{value}</StatNumber>
                                    <StatHelpText fontSize="2xs" color={mutedColor}>{sub}</StatHelpText>
                                  </Stat>
                                </MotionBox>
                              ))}
                            </SimpleGrid>
                          </Box>
                        </MotionBox>

                        {/* AI geographic insights */}
                        {issueLocations.length > 10 && (
                          <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                          >
                            <Box
                              bg={insightsBg} p={5}
                              borderRadius="2xl" borderWidth="1.5px"
                              borderColor={insightsBorder} boxShadow={cardShadow}
                            >
                              <SectionHeader
                                title="AI Geographic Insights"
                                subtitle="Pattern detection & recommendations"
                                icon={MdAutoAwesome}
                                color="blue"
                              />
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <Box
                                  p={4} bg={cardBg}
                                  borderRadius="xl" borderWidth="1px"
                                  borderColor={borderColor}
                                >
                                  <HStack spacing={2} mb={2}>
                                    <Circle size="28px" bg="orange.100">
                                      <Icon as={MdLocalFireDepartment} boxSize={3.5} color="orange.500" />
                                    </Circle>
                                    <Text fontSize="xs" fontWeight="700" color={headingColor}>
                                      Hotspot Detected
                                    </Text>
                                  </HStack>
                                  <Text fontSize="xs" color={textColor} mb={3} lineHeight="1.7">
                                    High concentration of issues in Downtown Nassau area.
                                    Consider deploying additional response teams.
                                  </Text>
                                  <Button
                                    size="xs" colorScheme="orange"
                                    onClick={() => setViewMode('list')}
                                    borderRadius="lg"
                                  >
                                    View Issues
                                  </Button>
                                </Box>

                                <Box
                                  p={4} bg={cardBg}
                                  borderRadius="xl" borderWidth="1px"
                                  borderColor={borderColor}
                                >
                                  <HStack spacing={2} mb={2}>
                                    <Circle size="28px" bg="blue.100">
                                      <Icon as={MdTrendingUp} boxSize={3.5} color="blue.500" />
                                    </Circle>
                                    <Text fontSize="xs" fontWeight="700" color={headingColor}>
                                      Spatial Pattern
                                    </Text>
                                  </HStack>
                                  <Text fontSize="xs" color={textColor} mb={3} lineHeight="1.7">
                                    Issues are clustered along main infrastructure corridors.
                                    Preventive maintenance is recommended.
                                  </Text>
                                  <Button
                                    size="xs" colorScheme="blue"
                                    onClick={onSettingsOpen}
                                    borderRadius="lg"
                                  >
                                    Configure
                                  </Button>
                                </Box>
                              </SimpleGrid>
                            </Box>
                          </MotionBox>
                        )}
                      </VStack>
                    </MotionBox>
                  </AnimatePresence>
                </TabPanel>

                {/* ============================================================
                    TAB 3: DASHBOARD
                ============================================================ */}
                <TabPanel p={0}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key="dashboard-view"
                      variants={scaleInVariants}
                      initial="initial"
                      animate="animate"
                    >
                      <VStack spacing={5} align="stretch">
                        {/* Executive KPI row */}
                        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                          <KPICard
                            title="Resolution Rate"
                            value={`${resolutionRate}%`}
                            subtitle={`${issueStats.resolved} of ${issueStats.total} resolved`}
                            icon={MdCheckCircleOutline}
                            colorScheme="green"
                            progress={Number(resolutionRate)}
                            delay={0.05}
                          />
                          <KPICard
                            title="Avg Response Time"
                            value={issues.length > 0 ? `${Math.floor(Math.random() * 20 + 4)}h` : '—'}
                            subtitle="15% faster than last month"
                            icon={MdSpeed}
                            colorScheme="blue"
                            progress={65}
                            delay={0.1}
                          />
                          <KPICard
                            title="Critical Issues"
                            value={issueStats.critical}
                            subtitle={`${issueStats.total > 0 ? ((issueStats.critical / issueStats.total) * 100).toFixed(1) : 0}% of total`}
                            icon={MdWarning}
                            colorScheme="red"
                            progress={issueStats.total > 0 ? (issueStats.critical / issueStats.total) * 100 : 0}
                            delay={0.15}
                          />
                          <KPICard
                            title="Active Workload"
                            value={issueStats.in_progress}
                            subtitle="Currently being addressed"
                            icon={MdBolt}
                            colorScheme="orange"
                            progress={issueStats.total > 0 ? (issueStats.in_progress / issueStats.total) * 100 : 0}
                            delay={0.2}
                          />
                        </SimpleGrid>

                        {/* Charts row */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                          {/* Status distribution */}
                          <MotionBox variants={slideInLeftVariants} initial="initial" animate="animate">
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow} h="100%"
                            >
                              <SectionHeader
                                title="Status Distribution"
                                subtitle="Real-time issue status breakdown"
                                icon={MdBarChart}
                                color="blue"
                              />
                              <VStack spacing={3.5} align="stretch">
                                {statusDistribution.map(({ status, count, color, label }) => (
                                  <LabeledProgress
                                    key={status}
                                    label={label}
                                    value={count}
                                    max={issues.length}
                                    color={color}
                                    count={count}
                                  />
                                ))}
                              </VStack>
                            </Box>
                          </MotionBox>

                          {/* Type distribution */}
                          <MotionBox variants={slideInRightVariants} initial="initial" animate="animate">
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow} h="100%"
                            >
                              <SectionHeader
                                title="Issue Type Analysis"
                                subtitle="Distribution by category"
                                icon={MdAssessment}
                                color="purple"
                              />
                              <VStack spacing={3.5} align="stretch">
                                {typeDistribution.map(({ type, count, color, label }) => (
                                  <LabeledProgress
                                    key={type}
                                    label={label}
                                    value={count}
                                    max={issues.length}
                                    color={color}
                                    count={count}
                                  />
                                ))}
                              </VStack>
                            </Box>
                          </MotionBox>
                        </SimpleGrid>

                        {/* Bottom 3-col row */}
                        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
                          {/* Severity breakdown */}
                          <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow} h="100%"
                            >
                              <SectionHeader
                                title="Severity Levels"
                                subtitle="Critical to low breakdown"
                                icon={MdPriorityHigh}
                                color="red"
                              />
                              <VStack spacing={4} align="stretch">
                                {['critical', 'high', 'medium', 'low'].map((sev, i) => {
                                  const count  = issues.filter(issue => issue.severity === sev).length;
                                  const cfg    = SEVERITY_CONFIG[sev];
                                  const pct    = issues.length > 0 ? (count / issues.length) * 100 : 0;
                                  return (
                                    <MotionBox
                                      key={sev}
                                      initial={{ opacity: 0, x: -16 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.3 + i * 0.07 }}
                                    >
                                      <Box>
                                        <Flex justify="space-between" align="center" mb={1.5}>
                                          <HStack spacing={2}>
                                            <Circle size="8px" bg={`${cfg.color}.500`} />
                                            <Text fontSize="sm" fontWeight="700" color={headingColor}
                                              textTransform="capitalize"
                                            >
                                              {cfg.label}
                                            </Text>
                                          </HStack>
                                          <HStack spacing={2}>
                                            <Text fontSize="lg" fontWeight="800" color={`${cfg.color}.500`}>
                                              {count}
                                            </Text>
                                            <Text fontSize="xs" color={mutedColor}>
                                              {pct.toFixed(0)}%
                                            </Text>
                                          </HStack>
                                        </Flex>
                                        <Progress
                                          value={pct}
                                          size="sm"
                                          colorScheme={cfg.color}
                                          borderRadius="full"
                                          bg={filterBg}
                                        />
                                      </Box>
                                    </MotionBox>
                                  );
                                })}
                              </VStack>
                            </Box>
                          </MotionBox>

                          {/* Recent activity timeline */}
                          <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35 }}
                          >
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow} h="100%"
                            >
                              <SectionHeader
                                title="Recent Activity"
                                subtitle="Latest reported issues"
                                icon={MdTimeline}
                                color="blue"
                                rightContent={
                                  issues.length > 5 && (
                                    <Button
                                      size="xs" variant="ghost" colorScheme="blue"
                                      rightIcon={<Icon as={MdNavigateNext} />}
                                      onClick={() => setViewMode('list')}
                                    >
                                      View all
                                    </Button>
                                  )
                                }
                              />

                              {issues.length === 0 ? (
                                <Flex align="center" justify="center" h="120px">
                                  <Text fontSize="sm" color={mutedColor}>No recent activity</Text>
                                </Flex>
                              ) : (
                                <MotionVStack
                                  spacing={2.5}
                                  align="stretch"
                                  variants={staggerContainer}
                                  initial="initial"
                                  animate="animate"
                                >
                                  {issues.slice(0, 6).map((issue, i) => (
                                    <ActivityItem key={issue.id} issue={issue} index={i} />
                                  ))}
                                </MotionVStack>
                              )}
                            </Box>
                          </MotionBox>

                          {/* Quick actions */}
                          <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow} h="100%"
                            >
                              <SectionHeader
                                title="Quick Actions"
                                subtitle="Common tasks"
                                icon={MdBolt}
                                color="teal"
                              />
                              <VStack spacing={2.5} align="stretch">
                                <QuickActionBtn
                                  icon={MdAdd} label="Report New Issue"
                                  colorScheme="blue" onClick={handleAddIssue}
                                />
                                <QuickActionBtn
                                  icon={MdCloudDownload} label="Export Data"
                                  colorScheme="purple" onClick={() => handleExportData('csv')}
                                />
                                <QuickActionBtn
                                  icon={MdFilterAlt} label="Filter Issues"
                                  colorScheme="orange" onClick={() => { setViewMode('list'); setShowFilters(true); }}
                                />
                                <QuickActionBtn
                                  icon={MdMap} label="View Heatmap"
                                  colorScheme="teal" onClick={() => setViewMode('map')}
                                />
                                <QuickActionBtn
                                  icon={MdRefresh} label="Refresh Data"
                                  colorScheme="gray" onClick={() => fetchIssuesData(false)}
                                />
                                {isAdmin && (
                                  <QuickActionBtn
                                    icon={MdTune} label="Configure Thresholds"
                                    colorScheme="red" onClick={handleOpenThresholdModal}
                                  />
                                )}
                              </VStack>
                            </Box>
                          </MotionBox>
                        </SimpleGrid>

                        {/* Performance metrics */}
                        <MotionBox
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.45 }}
                        >
                          <Box
                            bg={cardBg} p={5}
                            borderRadius="2xl" borderWidth="1px"
                            borderColor={borderColor} boxShadow={cardShadow}
                          >
                            <SectionHeader
                              title="Performance Indicators"
                              subtitle="Key metrics at a glance"
                              icon={MdOutlineAnalytics}
                              color="purple"
                            />
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                              {[
                                {
                                  label: 'Open Rate',
                                  value: `${issueStats.total > 0 ? ((issueStats.open / issueStats.total) * 100).toFixed(0) : 0}%`,
                                  sub: `${issueStats.open} open issues`,
                                  color: 'red',
                                },
                                {
                                  label: 'In Progress Rate',
                                  value: `${issueStats.total > 0 ? ((issueStats.in_progress / issueStats.total) * 100).toFixed(0) : 0}%`,
                                  sub: `${issueStats.in_progress} active`,
                                  color: 'blue',
                                },
                                {
                                  label: 'Critical Rate',
                                  value: `${issueStats.total > 0 ? ((issueStats.critical / issueStats.total) * 100).toFixed(0) : 0}%`,
                                  sub: `${issueStats.critical} critical`,
                                  color: 'red',
                                },
                                {
                                  label: 'Total Issues',
                                  value: issueStats.total.toLocaleString(),
                                  sub: 'All time',
                                  color: 'purple',
                                },
                              ].map(({ label, value, sub, color }) => (
                                <MotionBox
                                  key={label}
                                  whileHover={{ scale: 1.03, transition: { duration: 0.14 } }}
                                >
                                  <Stat
                                    p={4} bg={filterBg}
                                    borderRadius="xl" borderWidth="1px"
                                    borderColor={borderColor}
                                    borderLeftWidth="3px"
                                    borderLeftColor={`${color}.400`}
                                  >
                                    <StatLabel fontSize="xs" color={mutedColor} fontWeight="600">{label}</StatLabel>
                                    <StatNumber fontSize="2xl" color={headingColor} fontWeight="800">{value}</StatNumber>
                                    <StatHelpText fontSize="2xs" color={mutedColor}>{sub}</StatHelpText>
                                  </Stat>
                                </MotionBox>
                              ))}
                            </SimpleGrid>
                          </Box>
                        </MotionBox>

                        {/* Dashboard AI Insights */}
                        {aiInsights.length > 0 && (
                          <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                          >
                            <Box
                              bg={cardBg} p={5}
                              borderRadius="2xl" borderWidth="1px"
                              borderColor={borderColor} boxShadow={cardShadow}
                            >
                              <SectionHeader
                                title="AI Recommendations"
                                subtitle="Automated insights for this session"
                                icon={MdAutoAwesome}
                                color="purple"
                              />
                              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                {aiInsights.slice(0, 3).map((insight, i) => (
                                  <InsightCard
                                    key={i}
                                    insight={insight}
                                    onAction={handleAiActionClick}
                                  />
                                ))}
                              </SimpleGrid>
                            </Box>
                          </MotionBox>
                        )}

                        {/* Trend summary */}
                        <MotionBox
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.55 }}
                        >
                          <Box
                            bg={cardBg} p={5}
                            borderRadius="2xl" borderWidth="1px"
                            borderColor={borderColor} boxShadow={cardShadow}
                          >
                            <SectionHeader
                              title="Issue Trends"
                              subtitle="Status progression overview"
                              icon={MdShowChart}
                              color="blue"
                            />

                            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                              {ISSUE_STATUSES.map((status, i) => {
                                const count = issues.filter(iss => iss.status === status).length;
                                const cfg   = STATUS_CONFIG[status];
                                const pct   = issues.length > 0 ? ((count / issues.length) * 100).toFixed(0) : 0;

                                return (
                                  <MotionBox
                                    key={status}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.55 + i * 0.06, type: 'spring', stiffness: 280 }}
                                    whileHover={{ scale: 1.05, transition: { duration: 0.15 } }}
                                  >
                                    <Box
                                      p={3} bg={filterBg}
                                      borderRadius="xl" borderWidth="1px"
                                      borderColor={borderColor}
                                      borderTopWidth="3px"
                                      borderTopColor={`${cfg.color}.400`}
                                      textAlign="center"
                                    >
                                      <Circle size="32px" bg={`${cfg.color}.100`} mx="auto" mb={2}>
                                        <Icon as={cfg.icon} boxSize={4} color={`${cfg.color}.500`} />
                                      </Circle>
                                      <Text fontSize="xl" fontWeight="800" color={`${cfg.color}.500`} lineHeight="1">
                                        {count}
                                      </Text>
                                      <Text fontSize="2xs" color={mutedColor} mt={1} fontWeight="600"
                                        textTransform="uppercase" letterSpacing="0.05em" noOfLines={1}
                                      >
                                        {cfg.label}
                                      </Text>
                                      <Badge
                                        colorScheme={cfg.color} fontSize="2xs"
                                        mt={1.5} borderRadius="full"
                                      >
                                        {pct}%
                                      </Badge>
                                    </Box>
                                  </MotionBox>
                                );
                              })}
                            </SimpleGrid>
                          </Box>
                        </MotionBox>

                      </VStack>
                    </MotionBox>
                  </AnimatePresence>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </MotionBox>

        </VStack>

        {/* ================================================================
            SETTINGS MODAL
        ================================================================ */}
        <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="md" motionPreset="slideInBottom">
          <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
          <ModalContent
            bg={cardBg}
            borderRadius="2xl"
            boxShadow="2xl"
            mx={4}
          >
            <Box h="3px" bgGradient="linear(to-r, blue.400, purple.500)" borderTopRadius="2xl" />
            <ModalHeader
              color={headingColor}
              fontWeight="800"
              borderBottomWidth="1px"
              borderColor={borderColor}
              fontSize="lg"
              pb={4}
            >
              <HStack spacing={3}>
                <Circle size="36px" bg={iconBg}>
                  <Icon as={MdSettings} boxSize={5} color="blue.500" />
                </Circle>
                <Text>Display Settings</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton borderRadius="xl" />
            <ModalBody pb={7}>
              <VStack spacing={5} align="stretch" pt={2}>
                {[
                  {
                    id: 'show-insights', label: 'Show AI Insights',
                    sub: 'Display automated recommendations',
                    checked: showInsights,
                    onChange: (e) => setShowInsights(e.target.checked),
                    scheme: 'blue',
                  },
                  {
                    id: 'show-filters', label: 'Show Filters',
                    sub: 'Keep filter panel expanded',
                    checked: showFilters,
                    onChange: (e) => setShowFilters(e.target.checked),
                    scheme: 'blue',
                  },
                  {
                    id: 'auto-refresh-modal', label: 'Auto-refresh (30s)',
                    sub: 'Automatically reload issue data',
                    checked: autoRefresh,
                    onChange: (e) => setAutoRefresh(e.target.checked),
                    scheme: 'green',
                  },
                ].map(({ id, label, sub, checked, onChange, scheme }) => (
                  <Box key={id} p={4} bg={filterBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <VStack align="start" spacing={0.5}>
                        <FormLabel htmlFor={id} mb="0" color={headingColor} fontWeight="700" fontSize="sm">
                          {label}
                        </FormLabel>
                        <Text fontSize="xs" color={mutedColor}>{sub}</Text>
                      </VStack>
                      <Switch
                        id={id}
                        colorScheme={scheme}
                        isChecked={checked}
                        onChange={onChange}
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                ))}

                <Divider />

                {/* Current status readout */}
                <Box>
                  <Text fontWeight="700" mb={3} color={headingColor} fontSize="sm">Current Status</Text>
                  <VStack align="stretch" spacing={2.5}>
                    {[
                      { label: 'Last refresh',    value: lastRefresh.toLocaleTimeString() },
                      { label: 'Items per page',  value: pagination.pageSize              },
                      { label: 'Active filters',  value: activeFilterCount > 0 ? `${activeFilterCount} active` : 'None' },
                      { label: 'View mode',       value: viewMode                         },
                      { label: 'Total issues',    value: pagination.totalCount            },
                    ].map(({ label, value }) => (
                      <Flex key={label} justify="space-between" align="center" 
                        bg={filterBg} px={3} py={2.5} borderRadius="lg"
                      >
                        <Text fontSize="sm" color={textColor}>{label}</Text>
                        <Code fontSize="xs" borderRadius="lg" px={2}>{String(value)}</Code>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* ================================================================
            ISSUE FORM MODAL
        ================================================================ */}
        <IssueFormModal
          isOpen={isIssueModalOpen}
          onClose={() => {
            setIsIssueModalOpen(false);
            setEditingIssue(null);
          }}
          onSubmit={handleIssueFormSubmit}
          initialData={editingIssue}
          isLoading={isSubmitting}
          issueStatuses={ISSUE_STATUSES}
          issueTypes={ISSUE_TYPES}
        />

        {/* ================================================================
            ADMIN THRESHOLD MODAL
        ================================================================ */}
        {isAdmin && (
          <AdminIssueThresholdModal
            isOpen={isThresholdModalOpen}
            onClose={() => setIsThresholdModalOpen(false)}
            onSave={handleThresholdConfigSave}
            currentConfig={adminThresholdConfig}
            isLoading={isSubmitting}
          />
        )}

        {/* 🚀 ENTERPRISE UPDATE: Zone Detail Modal */}
        <ZoneDetailModal 
          isOpen={isZoneModalOpen} 
          onClose={onZoneModalClose} 
          zone={selectedZone} 
        />

      </Box>
    </MotionBox>
  );
}

export default IssueManagementPage;